import React, { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import * as chrono from "chrono-node";

// ── App config ───────────────────────────────────────────────────────────────
// TODO: point these at your real support inbox and policy pages before launch.
// The App Store requires a working support contact and a privacy policy URL.
const APP_VERSION  = "v37";
const PRIVACY_URL  = "https://kani-do.vercel.app/privacy.html";
const TERMS_URL    = "https://kani-do.vercel.app/terms.html";

// ── OneSignal Push Notifications ─────────────────────────────────────────────
const ONESIGNAL_APP_ID = "720f2440-fcd2-476e-9841-f7bc26c78bce";

function initOneSignal() {
  if (typeof window === "undefined") return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function(OneSignal) {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      safari_web_id: "web.onesignal.auto.2e21fe47-8329-4413-bae9-ecef4da3342d",
      notifyButton: { enable: false },
      allowLocalhostAsSecureOrigin: true,
    });
  });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Clean date to YYYY-MM-DD regardless of how it was stored
function cleanDate(d) {
  if (!d) return null;
  if (typeof d === "string" && d.includes("T")) return d.split("T")[0];
  if (typeof d === "string" && d.match(/^\d{4}-\d{2}-\d{2}$/)) return d;
  try { return new Date(d).toISOString().split("T")[0]; } catch { return null; }
}

function generateCode() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const buf = new Uint32Array(4);
  crypto.getRandomValues(buf);
  return Array.from(buf, n => chars[n % chars.length]).join("");
}

// Generates a share code that isn't already registered in user_codes.
async function generateUniqueCode() {
  for (let i = 0; i < 5; i++) {
    const code = generateCode();
    try {
      const { count, error } = await supabase.from("user_codes").select("code", { count: "exact", head: true }).eq("code", code);
      if (error) return code;
      if (!count) return code;
    } catch { return code; }
  }
  return generateCode();
}

// ideas.id is a numeric column, so ids stay numeric; the counter avoids
// same-millisecond collisions that a bare Date.now() produces.
let idCounter = 0;
function nextId() {
  return Date.now() * 1000 + (idCounter++ % 1000);
}

// Module-level bridge so module-scope helpers (saveProfile) can raise the
// same "Couldn't save" toast the component uses. App registers it on mount.
let reportSaveError = () => {};
function setSaveErrorReporter(fn) { reportSaveError = fn; }

async function loadIdeas(clientId) {
  try {
    const { data } = await supabase.from("ideas").select("*").eq("client_id", clientId).order("created_at", { ascending: false });
    return (data || []).map(i => ({ ...i, due_date: cleanDate(i.due_date) }));
  } catch { return []; }
}

async function loadIncoming(myCode) {
  try {
    const { data } = await supabase.from("friend_messages").select("*").eq("to_code", myCode).order("created_at", { ascending: false });
    return data || [];
  } catch { return []; }
}

async function sendMessage(toCode, fromCode, fromName, text) {
  try {
    await supabase.from("friend_messages").insert({ to_code: toCode, from_code: fromCode, from_name: fromName || fromCode, text, ts: new Date().toLocaleDateString() });
  } catch {}
}

async function deleteMessage(id) {
  try { await supabase.from("friend_messages").delete().eq("id", id); } catch {}
}

async function searchByUsername(username) {
  try {
    const { data } = await supabase.from("user_codes").select("code, username").ilike("username", username).limit(5);
    return data || [];
  } catch { return []; }
}

// ── Profile helpers ───────────────────────────────────────────────────────────

async function loadProfile(userId) {
  try {
    const { data } = await supabase.from("user_profiles").select("*").eq("id", userId).single();
    return data || null;
  } catch { return null; }
}

async function saveProfile(userId, updates) {
  try {
    const { error } = await supabase.from("user_profiles").upsert({ id: userId, ...updates });
    if (error) { reportSaveError(); return false; }
    return true;
  } catch { reportSaveError(); return false; }
}

async function saveCodeDB(code, username) {
  try { await supabase.from("user_codes").upsert({ code, username: username || null }); } catch {}
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STAGES = [
  { id: 0, name: "Tidal Pool",      emoji: "🌊", req: 0,  desc: "Complete tasks to start your adventure..." },
  { id: 1, name: "Sandy Shore",     emoji: "🏖️", req: 3,  desc: "The sand feels warm!" },
  { id: 2, name: "Kelp Forest",     emoji: "🌿", req: 8,  desc: "Getting adventurous!" },
  { id: 3, name: "Coral Reef",      emoji: "🪸", req: 15, desc: "Colourful and alive!" },
  { id: 4, name: "Open Ocean",      emoji: "🌊", req: 25, desc: "Out in the deep blue!" },
  { id: 5, name: "Sunken Ship",     emoji: "⚓",  req: 40, desc: "Hidden treasures await..." },
  { id: 6, name: "Volcanic Vent",   emoji: "🌋", req: 60, desc: "Hot stuff, Kani!" },
  { id: 7, name: "Treasure Island", emoji: "🏝️", req: 85, desc: "You found paradise!" },
];
function getStage(t) { let s=STAGES[0]; for(const st of STAGES){if(t>=st.req)s=st;} return s; }
function getNextStage(t) { return STAGES.find(s=>s.req>t)||null; }

const SUGGESTIONS = {
  treasure: [
    { cat: "🧘 Calm down",     color: "#00897B", tasks: ["Take 5 slow deep breaths","Step outside for 5 minutes","Make a warm drink and sit quietly","Do a 2-minute body scan","Put on one calming song","Splash cold water on your face","Write down 3 things you can see"] },
    { cat: "⚡ Quick wins",    color: "#F57C00", tasks: ["Clear your desk or one surface","Drink a full glass of water","Make your bed","Write tomorrow's top 3 tasks","Reply to one message you've been putting off","Wash the dishes","Put away anything out of place"] },
    { cat: "🏃 Move a little", color: "#C62828", tasks: ["Do 10 jumping jacks","Walk around the block","Stretch your neck and shoulders","Dance to one song","Do 5 minutes of yoga","Take the stairs","Stand up and shake out your hands"] },
    { cat: "🌱 Self-care",     color: "#6A1B9A", tasks: ["Put your phone down for 15 mins","Eat a piece of fruit","Write one thing you're proud of","Light a candle or open a window","Text someone you care about","Do something creative for 10 mins","Drink more water today"] },
    { cat: "🏠 Tidy up",       color: "#0277BD", tasks: ["Wipe down the kitchen counter","Take out the rubbish","Do one load of laundry","Vacuum one room","Clean the bathroom sink","Organise one drawer or shelf","Change your bed sheets"] },
    { cat: "🌙 Wind down",     color: "#4527A0", tasks: ["Turn off screens 30 mins before bed","Write 3 good things about today","Prep your outfit for tomorrow","Do a 5-min gentle stretch","Make a herbal tea","Dim the lights and relax","Take a warm shower"] },
    { cat: "☀️ Morning reset",  color: "#E65100", tasks: ["Make your bed","Drink a glass of water","Open the windows","Write down 3 things you want to do today","Eat something nourishing","Take 5 deep breaths","Step outside for fresh air"] },
    { cat: "💛 Social",        color: "#F9A825", tasks: ["Text someone you miss","Plan something to look forward to","Call a family member","Send a kind message to a friend","Make plans for the weekend","Write a thank you note","Check in on someone"] },
  ],
  work: [
    { cat: "📋 Get started",   color: "#0277BD", tasks: ["Write down your top 3 priorities for today","Clear your desktop","Open the most important task and do 5 minutes","Block focus time in your calendar","Close unnecessary tabs","Put your phone on silent","Make a cup of tea and sit down"] },
    { cat: "📧 Clear the backlog", color: "#00897B", tasks: ["Reply to one email you've been avoiding","Archive emails older than a month","Unsubscribe from one newsletter","Delete old drafts","Clear your spam folder","Flag the 3 most important emails","Send one follow-up you've been putting off"] },
    { cat: "💼 Career",        color: "#4527A0", tasks: ["Update your LinkedIn","Add a recent project to your portfolio","Write one line about what you've achieved this week","Connect with one person in your industry","Read one article in your field","Update your CV","Draft a bio or intro paragraph"] },
    { cat: "🗂 Organise",      color: "#6A1B9A", tasks: ["Clear your downloads folder","Archive old files","Create a folder for current projects","Delete duplicates","Back up important files","Organise your desktop","Clean up old screenshots"] },
    { cat: "🎯 Focus",         color: "#C62828", tasks: ["Turn off notifications for 30 minutes","Do one task from start to finish","Write a brain dump of everything on your mind","Prioritise your list by impact","Set a timer and work for 25 minutes","Remove one distraction from your workspace","Say no to one non-essential thing today"] },
    { cat: "🤝 People",        color: "#F57C00", tasks: ["Write a note of appreciation to a colleague","Follow up on something you promised","Schedule a catch-up with your team","Send a quick update to a stakeholder","Ask for feedback on something you've worked on","Recognise someone's contribution","Check in on a teammate"] },
  ],
  creative: [
    { cat: "✍️ Writing",       color: "#6A1B9A", tasks: ["Write for 10 minutes with no editing","Free write — stream of consciousness","Write one paragraph about your day","Start a journal entry","Write a list of ideas, no filter","Edit one paragraph you've already written","Write a short poem or haiku"] },
    { cat: "🎨 Visual",        color: "#C62828", tasks: ["Sketch something you can see right now","Doodle freely for 5 minutes","Start a mood board","Take 5 photos of interesting things around you","Pick 3 colours that represent your mood","Rearrange objects on your desk","Look for inspiration on Pinterest or Instagram"] },
    { cat: "🎵 Music",         color: "#0277BD", tasks: ["Listen to an album from start to finish","Learn one new chord","Hum or sing something for 5 minutes","Make a playlist for your current mood","Find one new artist you've never heard","Play an instrument for 10 minutes","Write lyrics for one verse"] },
    { cat: "📚 Reading & film", color: "#00897B", tasks: ["Read one chapter of your book","Watch a short film or documentary","Read an article about something you're curious about","Listen to a podcast episode","Re-read a favourite passage","Watch a TED talk","Find your next book to read"] },
    { cat: "💡 Inspiration",   color: "#F57C00", tasks: ["Find 3 things that inspire you today","Write down one idea you've been sitting on","Look at work by a creative you admire","Go for a walk and notice details","Visit a gallery, market or new place","Write down what you'd create if you had no limits","Save something that sparks joy"] },
    { cat: "🌿 Creative space", color: "#4527A0", tasks: ["Tidy your creative workspace","Organise your materials or tools","Clear out anything that doesn't inspire you","Light a candle or play background music","Set up your space for a creative session","Put away finished projects","Make your space feel like yours"] },
  ],
};

const CREATURES = [
  { id: "crab",       emoji: "🦀", name: "Crab",       unlockReq: 0,  unlockDesc: "" },
  { id: "dolphin",    emoji: "🐬", name: "Dolphin",    unlockReq: 0,  unlockDesc: "" },
  { id: "octopus",    emoji: "🐙", name: "Octopus",    unlockReq: 5,  unlockDesc: "5 tasks" },
  { id: "mermaid",    emoji: "🧜", name: "Mermaid",    unlockReq: 10, unlockDesc: "10 tasks" },
  { id: "turtle",     emoji: "🐢", name: "Turtle",     unlockReq: 15, unlockDesc: "15 tasks" },
  { id: "shark",      emoji: "🦈", name: "Shark",      unlockReq: 25, unlockDesc: "25 tasks" },
  { id: "whale",      emoji: "🐋", name: "Whale",      unlockReq: 40, unlockDesc: "40 tasks" },
  { id: "jellyfish",  emoji: "🪼", name: "Jellyfish",  unlockReq: 55, unlockDesc: "55 tasks" },
  { id: "anglerfish", emoji: "🐟", name: "Anglerfish", unlockReq: 70, unlockDesc: "70 tasks" },
];

const CHESTS = [
  { id: "treasure", defaultName: "Life",     emoji: "🐚", color: "#00897B", unlockReq: 0,    unlockDesc: "Always unlocked", desc: "Everyday tasks, self-care, and personal goals" },
  { id: "work",     defaultName: "Work",     emoji: "💼", color: "#0277BD", unlockReq: "pro", unlockDesc: "Upgrade to Pro", desc: "Career, productivity, and professional tasks" },
  { id: "creative", defaultName: "Creative", emoji: "✨", color: "#8E24AA", unlockReq: "pro", unlockDesc: "Upgrade to Pro", desc: "Writing, art, music, film, and inspiration" },
];

const ISLAND_BADGES = [
  { stageId: 0, badge: "🐚", label: "First Shell" },
  { stageId: 1, badge: "⭐", label: "Sea Star" },
  { stageId: 2, badge: "🪸", label: "Coral Crown" },
  { stageId: 3, badge: "🔱", label: "Neptune's Fork" },
  { stageId: 4, badge: "⚓", label: "Deep Anchor" },
  { stageId: 5, badge: "💎", label: "Sunken Gem" },
  { stageId: 6, badge: "🌋", label: "Magma Heart" },
  { stageId: 7, badge: "🏆", label: "Legend Trophy" },
];

const HATS = [
  { id: "none",    label: "No hat",       emoji: "",    unlockStage: null },
  { id: "captain", label: "Captain's Hat", emoji: "🪖", unlockStage: 7 },
  { id: "crown",   label: "Coral Crown",   emoji: "👑", unlockStage: 7 },
  { id: "pirate",  label: "Pirate Hat",    emoji: "🏴‍☠️", unlockStage: 7 },
];

const CHEST_LABELS = ["What do you need to do?","Add a task...","What's on your mind?","One thing at a time...","Add something...","What would help today?","What's been on your list?","Something small counts too..."];

// ── SVG Hat Components ────────────────────────────────────────────────────────

function HatCaptain({ size=84 }) {
  const s = size / 84;
  return (
    <svg width={size} height={size} viewBox="0 0 84 84" style={{ position:"absolute", top:0, left:0, pointerEvents:"none" }}>
      <ellipse cx="42" cy="22" rx="22" ry="5" fill="#1a237e"/>
      <rect x="25" y="6" width="34" height="17" rx="4" fill="#283593"/>
      <rect x="25" y="17" width="34" height="5" rx="2" fill="#e8eaf6"/>
      <text x="42" y="15" textAnchor="middle" fontSize={10*s} fill="#FFD700">⚓</text>
    </svg>
  );
}

function HatCrown({ size=84 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 84 84" style={{ position:"absolute", top:0, left:0, pointerEvents:"none" }}>
      <path d="M22 22 L26 8 L34 16 L42 4 L50 16 L58 8 L62 22 Z" fill="#FFB300"/>
      <rect x="22" y="20" width="40" height="6" rx="2" fill="#FF8F00"/>
      <circle cx="32" cy="17" r="3" fill="#e74c3c"/>
      <circle cx="42" cy="12" r="3.5" fill="#26C6DA"/>
      <circle cx="52" cy="17" r="3" fill="#e74c3c"/>
    </svg>
  );
}

function HatPirate({ size=84 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 84 84" style={{ position:"absolute", top:0, left:0, pointerEvents:"none" }}>
      <ellipse cx="42" cy="22" rx="28" ry="6" fill="#1a1a1a"/>
      <path d="M28 22 Q28 4 42 4 Q56 4 56 22 Z" fill="#212121"/>
      <circle cx="42" cy="14" r="5" fill="white"/>
      <circle cx="39.5" cy="13" r="1.5" fill="#212121"/>
      <circle cx="44.5" cy="13" r="1.5" fill="#212121"/>
      <path d="M39 17 Q42 19 45 17" stroke="#212121" strokeWidth="1.2" fill="none"/>
      <line x1="36" y1="19" x2="48" y2="19" stroke="white" strokeWidth="1.5"/>
    </svg>
  );
}

function HatOverlay({ hatId, size }) {
  if (hatId === "captain") return <HatCaptain size={size}/>;
  if (hatId === "crown")   return <HatCrown size={size}/>;
  if (hatId === "pirate")  return <HatPirate size={size}/>;
  return null;
}

// ── Creature SVGs ─────────────────────────────────────────────────────────────

function Crab({ size=72, animate=false, smiling=false }) {
  const eyeY = smiling ? 17 : 19;
  const mouthPath = smiling ? "M 24 29 Q 32 38 40 29" : "M 27 31 Q 32 28 37 31";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:animate?"drop-shadow(0 6px 16px #FF8A7166)":"drop-shadow(0 3px 6px rgba(0,0,0,0.15))", transition:"all .4s" }}>
      <line x1="10" y1="36" x2="2"  y2="27" stroke="#c0392b" strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="10" y1="41" x2="1"  y2="37" stroke="#c0392b" strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="54" y1="36" x2="62" y2="27" stroke="#c0392b" strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="54" y1="41" x2="63" y2="37" stroke="#c0392b" strokeWidth="3.5" strokeLinecap="round"/>
      <ellipse cx="7"  cy="23" rx="6.5" ry="5" fill="#e74c3c" transform="rotate(-30 7 23)"/>
      <ellipse cx="57" cy="23" rx="6.5" ry="5" fill="#e74c3c" transform="rotate(30 57 23)"/>
      <line x1="13" y1="32" x2="6"  y2="21" stroke="#c0392b" strokeWidth="4.5" strokeLinecap="round"/>
      <line x1="51" y1="32" x2="58" y2="21" stroke="#c0392b" strokeWidth="4.5" strokeLinecap="round"/>
      <ellipse cx="32" cy="38" rx="22" ry="14" fill="#e74c3c"/>
      <ellipse cx="32" cy="23" rx="14.5" ry="11.5" fill="#e74c3c"/>
      <circle cx="25" cy={eyeY} r="5.5" fill="white"/>
      <circle cx="39" cy={eyeY} r="5.5" fill="white"/>
      {smiling && <><ellipse cx="25" cy={eyeY-3} rx="5.5" ry="3" fill="#e74c3c"/><ellipse cx="39" cy={eyeY-3} rx="5.5" ry="3" fill="#e74c3c"/></>}
      <circle cx="26" cy={eyeY+1} r="2.8" fill="#1a0800"/>
      <circle cx="40" cy={eyeY+1} r="2.8" fill="#1a0800"/>
      <circle cx="27" cy={eyeY-.5} r="1.1" fill="white"/>
      <circle cx="41" cy={eyeY-.5} r="1.1" fill="white"/>
      <path d={mouthPath} stroke={smiling?"#fff":"#c0392b"} strokeWidth={smiling?2.5:2} fill="none" strokeLinecap="round"/>
      {smiling && <><ellipse cx="21" cy="27" rx="4.5" ry="2.5" fill="#ff8a80" opacity="0.75"/><ellipse cx="43" cy="27" rx="4.5" ry="2.5" fill="#ff8a80" opacity="0.75"/></>}
      <line x1="25" y1="12" x2="25" y2="18" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="39" y1="12" x2="39" y2="18" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function Dolphin({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <path d="M 8 36 Q 4 28 2 38 Q 6 44 10 38 Z" fill="#4FC3F7"/>
      <path d="M 8 36 Q 4 44 2 34 Q 6 28 10 36 Z" fill="#29B6F6"/>
      <ellipse cx="30" cy="36" rx="22" ry="10" fill="#4FC3F7"/>
      <ellipse cx="30" cy="38" rx="16" ry="6" fill="#E1F5FE"/>
      <path d="M 32 26 Q 38 18 44 26 Z" fill="#29B6F6"/>
      <path d="M 50 32 Q 60 34 58 38 Q 54 36 50 38 Z" fill="#4FC3F7"/>
      <circle cx="48" cy="32" r="2.5" fill="#1a3a4a"/>
      <circle cx="49" cy="31" r="0.8" fill="white"/>
      <path d="M 36 38 Q 30 46 22 44 Q 28 40 36 38 Z" fill="#29B6F6"/>
    </svg>
  );
}

function Octopus({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      {[0,1,2,3,4,5,6,7].map(i => { const a=(i/8)*Math.PI*2, x1=32+Math.cos(a)*14, y1=36+Math.sin(a)*10, x2=32+Math.cos(a)*28, y2=54+Math.sin(a)*6, cx=32+Math.cos(a+.3)*22, cy=46+Math.sin(a)*8; return <path key={i} d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`} stroke="#AB47BC" strokeWidth="4" fill="none" strokeLinecap="round"/>; })}
      <ellipse cx="32" cy="28" rx="18" ry="16" fill="#CE93D8"/>
      <ellipse cx="32" cy="18" rx="12" ry="10" fill="#BA68C8"/>
      <circle cx="25" cy="26" r="5" fill="white"/><circle cx="39" cy="26" r="5" fill="white"/>
      <circle cx="26" cy="27" r="2.5" fill="#4a0080"/><circle cx="40" cy="27" r="2.5" fill="#4a0080"/>
      <path d="M 26 33 Q 32 38 38 33" stroke="#9C27B0" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function Mermaid({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <path d="M 24 54 Q 18 62 14 58 Q 20 52 24 54 Z" fill="#26C6DA"/>
      <path d="M 24 54 Q 30 62 34 58 Q 28 52 24 54 Z" fill="#00ACC1"/>
      <path d="M 20 40 Q 16 50 24 54 Q 28 50 24 40 Z" fill="#26C6DA"/>
      <ellipse cx="26" cy="32" rx="10" ry="12" fill="#FFCCBC"/>
      <ellipse cx="22" cy="30" rx="5" ry="3.5" fill="#26C6DA" transform="rotate(-10 22 30)"/>
      <ellipse cx="30" cy="30" rx="5" ry="3.5" fill="#26C6DA" transform="rotate(10 30 30)"/>
      <path d="M 16 28 Q 10 32 12 38" stroke="#FFCCBC" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <path d="M 36 28 Q 42 32 40 38" stroke="#FFCCBC" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <circle cx="26" cy="18" r="11" fill="#FFCCBC"/>
      <path d="M 16 14 Q 14 24 16 32" stroke="#F06292" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <path d="M 36 14 Q 38 24 36 32" stroke="#F06292" strokeWidth="4" fill="none" strokeLinecap="round"/>
      <ellipse cx="26" cy="12" rx="11" ry="7" fill="#F06292"/>
      <circle cx="22" cy="18" r="2.5" fill="#4a2800"/><circle cx="30" cy="18" r="2.5" fill="#4a2800"/>
      <path d="M 22 23 Q 26 27 30 23" stroke="#e57373" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function Turtle({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <ellipse cx="32" cy="36" rx="10" ry="6" fill="#388E3C"/>
      <ellipse cx="32" cy="30" rx="18" ry="16" fill="#4CAF50"/>
      <ellipse cx="32" cy="30" rx="14" ry="12" fill="#388E3C"/>
      <path d="M 24 20 Q 32 14 40 20" stroke="#2E7D32" strokeWidth="2" fill="none"/>
      <path d="M 20 28 Q 14 22 16 30" stroke="#4CAF50" strokeWidth="5" strokeLinecap="round" fill="none"/>
      <path d="M 44 28 Q 50 22 48 30" stroke="#4CAF50" strokeWidth="5" strokeLinecap="round" fill="none"/>
      <ellipse cx="32" cy="18" rx="8" ry="6" fill="#A5D6A7"/>
      <circle cx="29" cy="17" r="2" fill="#1a3a1a"/><circle cx="35" cy="17" r="2" fill="#1a3a1a"/>
      <path d="M 29 21 Q 32 24 35 21" stroke="#388E3C" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function Shark({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <path d="M 4 38 Q 2 30 8 34 Z" fill="#607D8B"/>
      <path d="M 4 38 Q 2 44 8 40 Z" fill="#546E7A"/>
      <ellipse cx="30" cy="38" rx="24" ry="10" fill="#78909C"/>
      <ellipse cx="28" cy="40" rx="18" ry="6" fill="#B0BEC5"/>
      <path d="M 22 28 Q 28 16 34 28 Z" fill="#607D8B"/>
      <path d="M 44 38 Q 54 32 56 40 Q 50 44 44 38 Z" fill="#607D8B"/>
      <circle cx="46" cy="34" r="3" fill="#1a2a34"/>
      <circle cx="47" cy="33" r="1" fill="white"/>
      <path d="M 28 44 Q 36 50 42 44" stroke="#546E7A" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <line x1="30" y1="44" x2="29" y2="48" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="36" y1="46" x2="36" y2="50" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function Whale({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <path d="M 8 38 Q 4 32 2 40 Q 6 46 10 40 Z" fill="#1565C0"/>
      <path d="M 8 38 Q 4 44 2 36 Q 6 30 10 38 Z" fill="#1976D2"/>
      <ellipse cx="32" cy="36" rx="24" ry="14" fill="#1E88E5"/>
      <ellipse cx="28" cy="38" rx="18" ry="8" fill="#BBDEFB"/>
      <path d="M 36 22 Q 44 12 50 22 Z" fill="#1565C0"/>
      <circle cx="46" cy="30" r="4" fill="white"/>
      <circle cx="47" cy="30" r="2" fill="#0D47A1"/>
      <circle cx="48" cy="29" r="0.8" fill="white"/>
      <path d="M 20 38 Q 28 46 36 38" stroke="#1565C0" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <ellipse cx="50" cy="22" rx="3" ry="5" fill="#29B6F6" opacity="0.7"/>
    </svg>
  );
}

function Jellyfish({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      {[0,1,2,3,4].map(i => { const x=18+i*7; return <path key={i} d={`M ${x} 36 Q ${x-3} 50 ${x} 58 Q ${x+3} 50 ${x+4} 36`} stroke="#F48FB1" strokeWidth="2.5" fill="none" strokeLinecap="round"/>; })}
      <ellipse cx="32" cy="26" rx="20" ry="16" fill="#F8BBD0"/>
      <ellipse cx="32" cy="22" rx="16" ry="10" fill="#FCE4EC"/>
      <ellipse cx="32" cy="28" rx="20" ry="8" fill="#F48FB1" opacity="0.4"/>
      <circle cx="26" cy="24" r="3.5" fill="white"/><circle cx="38" cy="24" r="3.5" fill="white"/>
      <circle cx="27" cy="25" r="1.8" fill="#880E4F"/><circle cx="39" cy="25" r="1.8" fill="#880E4F"/>
      <path d="M 27 30 Q 32 35 37 30" stroke="#F48FB1" strokeWidth="2" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function Anglerfish({ size=56 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ display:"block", filter:"drop-shadow(0 3px 6px rgba(0,0,0,0.12))" }}>
      <ellipse cx="32" cy="36" rx="22" ry="14" fill="#455A64"/>
      <ellipse cx="28" cy="32" rx="18" ry="12" fill="#546E7A"/>
      <ellipse cx="28" cy="40" rx="14" ry="6" fill="#607D8B" opacity="0.6"/>
      <path d="M 20 22 Q 26 14 32 22" fill="#37474F"/>
      <path d="M 40 38 Q 50 44 46 50 Q 38 46 40 38 Z" fill="#37474F"/>
      <circle cx="18" cy="30" r="9" fill="white"/>
      <circle cx="18" cy="30" r="6" fill="#1A237E"/>
      <circle cx="18" cy="30" r="3" fill="#0D47A1"/>
      <circle cx="20" cy="27" r="2" fill="white"/>
      <path d="M 20 42 Q 28 48 36 42" stroke="#37474F" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <line x1="23" y1="43" x2="22" y2="46" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="28" y1="45" x2="28" y2="48" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="33" y1="43" x2="34" y2="46" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function CreatureIcon({ id, size=56, smiling=false, animate=false }) {
  if (id==="dolphin")    return <Dolphin size={size}/>;
  if (id==="octopus")    return <Octopus size={size}/>;
  if (id==="mermaid")    return <Mermaid size={size}/>;
  if (id==="turtle")     return <Turtle size={size}/>;
  if (id==="shark")      return <Shark size={size}/>;
  if (id==="whale")      return <Whale size={size}/>;
  if (id==="jellyfish")  return <Jellyfish size={size}/>;
  if (id==="anglerfish") return <Anglerfish size={size}/>;
  return <Crab size={size} smiling={smiling} animate={animate}/>;
}

function CreatureWithAccessory({ id, size=84, smiling=false, animate=false, hatId="none" }) {
  return (
    <div style={{ position:"relative", display:"inline-block", width:size, height:size }}>
      <CreatureIcon id={id} size={size} smiling={smiling} animate={animate}/>
      <HatOverlay hatId={hatId} size={size}/>
    </div>
  );
}

function Confetti({ active }) {
  const pieces = useMemo(() => Array.from({ length: 26 }, (_, i) => ({ x:5+Math.random()*90, color:["#FF8A71","#FFB300","#4ecdc4","#a29bfe","#2ecc71","#e74c3c","#f06292"][i%7], delay:Math.random()*.65, size:7+Math.random()*8, dur:1.4+Math.random()*.8, round:Math.random()>.5 })), []);
  if (!active) return null;
  return (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:300, overflow:"hidden" }}>
      <style>{`@keyframes confettiFall{to{transform:translateY(110vh) rotate(800deg);opacity:0}}`}</style>
      {pieces.map((p,i) => <div key={i} style={{ position:"absolute", left:p.x+"%", top:"-24px", width:p.size, height:p.size, borderRadius:p.round?"50%":"3px", background:p.color, animation:`confettiFall ${p.dur}s ${p.delay}s ease-in forwards` }}/>)}
    </div>
  );
}

// ── Magic Link Login Screen ───────────────────────────────────────────────────

function PasswordResetScreen({ onDone }) {
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState(false);

  const C = { bgFrom:"#4DB6AC", bgTo:"#006064", coral:"#FF8A71", navText:"#002D2F", muted:"#4a8c8e", inputBg:"#E0F7FA", inputBorder:"#80DEEA", accent2:"#00897B" };

  const handleReset = async () => {
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setSuccess(true);
    setTimeout(() => onDone(), 2000);
  };

  return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,${C.bgFrom},${C.bgTo})`, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <style>{`*,*::before,*::after{box-sizing:border-box;margin:0;padding:0} body{font-family:'Nunito',sans-serif}`}</style>
      <div style={{ background:"#fff", borderRadius:28, padding:"36px 24px", maxWidth:380, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", textAlign:"center" }}>
        <div style={{ marginBottom:12 }}><Crab size={64} smiling={success}/></div>
        <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:24, fontWeight:700, color:C.navText, marginBottom:8 }}>
          {success ? "Password updated! 🎉" : "Set a new password 🔑"}
        </div>
        {success ? (
          <div style={{ fontSize:14, color:C.muted, lineHeight:1.7 }}>Logging you in now... 🌊</div>
        ) : (
          <div style={{ textAlign:"left" }}>
            <div style={{ fontSize:14, color:C.muted, marginBottom:20, lineHeight:1.7, textAlign:"center" }}>Choose a strong new password for your Kani Do account.</div>
            <input type="password" placeholder="New password" value={password}
              onChange={e=>{ setPassword(e.target.value); setError(""); }}
              style={{ display:"block", width:"100%", background:C.inputBg, border:`1.5px solid ${error?C.coral:C.inputBorder}`, borderRadius:14, padding:"13px 16px", color:C.navText, fontSize:15, outline:"none", marginBottom:10, fontFamily:"'Nunito',sans-serif" }}
            />
            <input type="password" placeholder="Confirm password" value={confirm}
              onChange={e=>{ setConfirm(e.target.value); setError(""); }}
              onKeyDown={e=>e.key==="Enter"&&handleReset()}
              style={{ display:"block", width:"100%", background:C.inputBg, border:`1.5px solid ${error?C.coral:C.inputBorder}`, borderRadius:14, padding:"13px 16px", color:C.navText, fontSize:15, outline:"none", marginBottom:10, fontFamily:"'Nunito',sans-serif" }}
            />
            {error && <div style={{ color:C.coral, fontSize:12, fontWeight:700, marginBottom:10 }}>{error}</div>}
            <button onClick={handleReset} disabled={loading}
              style={{ display:"block", width:"100%", padding:"14px", background:`linear-gradient(130deg,${C.coral},#FF5252)`, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", opacity:loading?0.7:1, fontFamily:"'Nunito',sans-serif", boxShadow:"0 4px 16px rgba(255,138,113,0.4)" }}>
              {loading ? "Updating..." : "Set new password 🌊"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [sent, setSent]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [mode, setMode]         = useState("password"); // "password" | "magic"
  const [forgotSent, setForgotSent] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const C = { bgFrom:"#4DB6AC", bgTo:"#006064", coral:"#FF8A71", navText:"#002D2F", muted:"#4a8c8e", inputBg:"#E0F7FA", inputBorder:"#80DEEA", accent2:"#00897B" };

  const sendPasswordReset = async () => {
    if (!email.trim() || !email.includes("@")) { setError("Please enter your email first"); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setForgotSent(true);
  };

  const inputStyle = (hasError) => ({
    width:"100%", background:C.inputBg, border:`1.5px solid ${hasError?C.coral:C.inputBorder}`,
    borderRadius:14, padding:"13px 16px", color:C.navText, fontSize:15, outline:"none",
    marginBottom:10, fontFamily:"'Nunito',sans-serif"
  });

  const handlePassword = async () => {
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true); setError("");
    if (isSignUp) {
      const { error: err } = await supabase.auth.signUp({ email: email.trim(), password });
      setLoading(false);
      if (err) { setError(err.message); return; }
      setSent(true);
    } else {
      const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      setLoading(false);
      if (err) { setError(err.message === "Invalid login credentials" ? "Wrong email or password — try again!" : err.message); return; }
    }
  };

  const sendMagicLink = async () => {
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email"); return; }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin }
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setSent(true);
  };

  return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,${C.bgFrom} 0%,${C.bgTo} 100%)`, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;600;700&family=Nunito:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;margin:0;padding:0} body{font-family:'Nunito',sans-serif}`}</style>
      <div style={{ background:"#fff", borderRadius:28, padding:"36px 28px", maxWidth:360, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", textAlign:"center" }}>
        <div style={{ marginBottom:8 }}><Crab size={72} smiling={true}/></div>
        <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:32, fontWeight:700, color:C.navText, marginBottom:4 }}>
          Kani <span style={{ color:C.coral }}>Do</span> 🦀
        </div>
        <div style={{ fontSize:12, color:C.muted, letterSpacing:2.5, textTransform:"uppercase", fontWeight:700, marginBottom:16 }}>
          Navigate your day, one island at a time
        </div>

        {sent ? (
          <div>
            <div style={{ fontSize:48, marginBottom:16 }}>📬</div>
            <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:700, color:C.navText, marginBottom:10 }}>
              {isSignUp ? "Check your email!" : "Magic link sent!"}
            </div>
            <div style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:20 }}>
              We sent a link to <strong>{email}</strong>.<br/>
              {isSignUp ? "Click it to confirm your account! 🌊" : "Tap it to log in — no password needed! 🌊"}
            </div>
            <button onClick={()=>{ setSent(false); setEmail(""); setPassword(""); }}
              style={{ background:"transparent", border:`1.5px solid ${C.inputBorder}`, borderRadius:12, padding:"10px 20px", color:C.muted, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
              ← Go back
            </button>
          </div>
        ) : (
          <div>
            {/* Mode toggle */}
            <div style={{ display:"flex", gap:6, marginBottom:20, background:C.inputBg, borderRadius:12, padding:4 }}>
              <button onClick={()=>{ setMode("password"); setError(""); }}
                style={{ flex:1, padding:"9px", borderRadius:9, border:"none", background:mode==="password"?"#fff":"transparent", color:mode==="password"?C.navText:C.muted, fontSize:13, fontWeight:800, cursor:"pointer", boxShadow:mode==="password"?"0 2px 8px rgba(0,0,0,0.08)":"none", fontFamily:"'Nunito',sans-serif" }}>
                🔑 Password
              </button>
              <button onClick={()=>{ setMode("magic"); setError(""); setIsSignUp(false); }}
                style={{ flex:1, padding:"9px", borderRadius:9, border:"none", background:mode==="magic"?"#fff":"transparent", color:mode==="magic"?C.navText:C.muted, fontSize:13, fontWeight:800, cursor:"pointer", boxShadow:mode==="magic"?"0 2px 8px rgba(0,0,0,0.08)":"none", fontFamily:"'Nunito',sans-serif" }}>
                ✨ Magic link
              </button>
            </div>

            <input type="email" placeholder="your@email.com" value={email}
              onChange={e=>{ setEmail(e.target.value); setError(""); }}
              onKeyDown={e=>e.key==="Enter"&&(mode==="password"?handlePassword():sendMagicLink())}
              style={inputStyle(!!error)}
            />

            {mode === "password" && (
              <input type="password" placeholder="Password (min 6 chars)" value={password}
                onChange={e=>{ setPassword(e.target.value); setError(""); }}
                onKeyDown={e=>e.key==="Enter"&&handlePassword()}
                style={inputStyle(false)}
              />
            )}

            {error && <div style={{ color:C.coral, fontSize:12, fontWeight:700, marginBottom:10, textAlign:"left" }}>{error}</div>}

            {mode === "password" ? (
              <>
                <button onClick={handlePassword} disabled={loading}
                  style={{ width:"100%", padding:"14px", background:`linear-gradient(130deg,${C.coral},#FF5252)`, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", boxShadow:"0 4px 16px rgba(255,138,113,0.4)", opacity:loading?0.7:1, marginBottom:10, fontFamily:"'Nunito',sans-serif" }}>
                  {loading ? "..." : isSignUp ? "🌊 Create account" : "🌊 Log in"}
                </button>
                <button onClick={()=>{ setIsSignUp(v=>!v); setError(""); }}
                  style={{ background:"transparent", border:"none", color:C.muted, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
                  {isSignUp ? "Already have an account? Log in →" : "New here? Create an account →"}
                </button>
                {!isSignUp && !forgotSent && (
                  <button onClick={sendPasswordReset} disabled={loading}
                    style={{ background:"transparent", border:"none", color:C.coral, fontSize:12, fontWeight:700, cursor:"pointer", marginTop:6, fontFamily:"'Nunito',sans-serif", opacity:loading?0.6:1 }}>
                    Forgot password? →
                  </button>
                )}
                {forgotSent && (
                  <div style={{ background:"#E0F7FA", borderRadius:10, padding:"10px 14px", marginTop:10, fontSize:12, color:C.accent2, fontWeight:700 }}>
                    📬 Reset link sent to {email}!
                  </div>
                )}
              </>
            ) : (
              <button onClick={sendMagicLink} disabled={loading}
                style={{ width:"100%", padding:"14px", background:`linear-gradient(130deg,${C.coral},#FF5252)`, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", boxShadow:"0 4px 16px rgba(255,138,113,0.4)", opacity:loading?0.7:1, fontFamily:"'Nunito',sans-serif" }}>
                {loading ? "Sending..." : "🌊 Send magic link"}
              </button>
            )}

            <div style={{ fontSize:11, color:C.muted, marginTop:14, lineHeight:1.6 }}>
              Your data syncs across all your devices 🦀
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

// ── Share Card Modal ─────────────────────────────────────────────────────────
function ShareCardModal({ show, onClose, myName, totalDone, streak, stage, myCreature, C, showToast }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!show) { setImgUrl(null); return; }
    setGenerating(true);
    generateShareImage({ name: myName, totalDone, streak, stageName: stage.name, stageEmoji: stage.emoji, creature: myCreature })
      .then(url => { setImgUrl(url); setGenerating(false); })
      .catch(() => setGenerating(false));
  }, [show]);

  if (!show) return null;

  const handleShare = async () => {
    if (imgUrl && navigator.share) {
      try {
        const res = await fetch(imgUrl);
        const blob = await res.blob();
        const file = new File([blob], "kani-do-progress.png", { type:"image/png" });
        await navigator.share({ title:"My Kani Do progress", files:[file] });
        onClose();
        return;
      } catch {}
    }
    // Fallback — download
    if (imgUrl) {
      const a = document.createElement("a");
      a.href = imgUrl;
      a.download = "kani-do-progress.png";
      a.click();
    }
    onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:24, padding:"24px 20px", maxWidth:340, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.3)", animation:"popIn .3s ease" }} onClick={e=>e.stopPropagation()}>
        <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:20, fontWeight:700, color:"#002D2F", textAlign:"center", marginBottom:16 }}>Your progress card 🌊</div>

        {generating ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"#4a8c8e", fontSize:14 }}>
            <div style={{ animation:"bob 1s infinite", display:"inline-block", fontSize:36, marginBottom:12 }}>🦀</div>
            <div>Generating your card...</div>
          </div>
        ) : imgUrl ? (
          <img src={imgUrl} alt="Your Kani Do progress" style={{ width:"100%", borderRadius:16, marginBottom:16, display:"block" }}/>
        ) : null}

        {imgUrl && (
          <>
            <button onClick={handleShare}
              style={{ width:"100%", padding:"14px", background:"linear-gradient(130deg,#FF8A71,#FF5252)", border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", marginBottom:8, fontFamily:"'Nunito',sans-serif", boxShadow:"0 4px 16px rgba(255,138,113,0.4)" }}>
              Share 🌊
            </button>
            <button onClick={()=>{ const a=document.createElement("a"); a.href=imgUrl; a.download="kani-do-progress.png"; a.click(); }}
              style={{ width:"100%", padding:"12px", background:"#E0F7FA", border:"none", borderRadius:14, color:"#002D2F", fontSize:14, fontWeight:800, cursor:"pointer", marginBottom:8, fontFamily:"'Nunito',sans-serif" }}>
              Download image
            </button>
          </>
        )}
        <button onClick={onClose}
          style={{ width:"100%", padding:"11px", background:"transparent", border:"none", color:"#aaa", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
          Close
        </button>
      </div>
    </div>
  );
}

// ── Share Card Generator ─────────────────────────────────────────────────────
async function generateShareImage({ name, totalDone, streak, stageName, stageEmoji, creature }) {
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 800;
  const ctx = canvas.getContext("2d");

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 600, 800);
  grad.addColorStop(0, "#4DB6AC");
  grad.addColorStop(1, "#006064");
  ctx.fillStyle = grad;
  ctx.roundRect(0, 0, 600, 800, 40);
  ctx.fill();

  // Decorative circles
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.beginPath(); ctx.arc(500, 100, 180, 0, Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.arc(80, 700, 140, 0, Math.PI*2); ctx.fill();

  // Load Fredoka font
  await document.fonts.load("700 48px Fredoka");

  // Kani Do title
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.font = "600 22px Fredoka";
  ctx.textAlign = "center";
  ctx.fillText("KANI DO", 300, 70);

  // Creature emoji (large)
  ctx.font = "140px serif";
  ctx.textAlign = "center";
  const creatureEmojis = {
    crab:"🦀", dolphin:"🐬", octopus:"🐙", mermaid:"🧜",
    turtle:"🐢", shark:"🦈", whale:"🐋", jellyfish:"🪼", anglerfish:"🐟"
  };
  ctx.fillText(creatureEmojis[creature]||"🦀", 300, 260);

  // Username
  ctx.fillStyle = "#fff";
  ctx.font = "700 48px Fredoka";
  ctx.textAlign = "center";
  ctx.fillText(name || "Kani Do", 300, 330);

  // Divider
  ctx.fillStyle = "rgba(255,255,255,0.2)";
  ctx.fillRect(80, 355, 440, 1.5);

  // Stats
  const stats = [
    { value: String(totalDone), label: "TASKS DONE", x: 150 },
    { value: streak > 0 ? String(streak) : "-", label: "DAY STREAK", x: 300 },
    { value: stageEmoji, label: stageName.toUpperCase(), x: 450 },
  ];

  stats.forEach(stat => {
    ctx.fillStyle = stat.label === "DAY STREAK" && streak > 0 ? "#FFB300" : "#fff";
    ctx.font = "700 52px Fredoka";
    ctx.textAlign = "center";
    ctx.fillText(stat.value, stat.x, 440);
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "500 14px Nunito, sans-serif";
    ctx.fillText(stat.label, stat.x, 465);
  });

  // Motivational line
  const lines = [
    "Every task is a wave I've ridden.",
    "One task at a time. 🌊",
    "Progress, not perfection.",
    "Navigating my day, one island at a time.",
    "Small wins add up. 🐚",
  ];
  const line = lines[totalDone % lines.length];
  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.font = "600 22px Nunito, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(line, 300, 560);

  // kanido.co
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.font = "500 18px Nunito, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("kanido.co", 300, 740);

  return canvas.toDataURL("image/png");
}


function ProCheckoutButton({ plan, userId, label, bg, shadow, showToast }) {
  const [loading, setLoading] = React.useState(false);
  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, userId }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; }
      else { showToast("Something went wrong — try again 🦀"); setLoading(false); }
    } catch { showToast("Could not connect to checkout 🦀"); setLoading(false); }
  };
  return (
    <button onClick={handleCheckout} disabled={loading}
      style={{ width:"100%", padding:"14px", background:bg, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, marginBottom:8, boxShadow:`0 4px 16px ${shadow}`, fontFamily:"'Nunito',sans-serif", opacity:loading?0.75:1 }}>
      {loading ? "Opening checkout..." : label}
    </button>
  );
}

export default function App() {
  const [user, setUser]               = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [ready, setReady]             = useState(false);
  const [tab, setTab]                 = useState("chest");
  const [ideas, setIdeas]             = useState([]);
  const [newIdea, setNewIdea]         = useState("");
  const [parsedDate, setParsedDate]   = useState(null);
  const [parsedTime, setParsedTime]   = useState(null);
  const [parsedText, setParsedText]   = useState("");
  const [shuffled, setShuffled]       = useState(null);
  const [myCode, setMyCode]           = useState("");
  const [myName, setMyName]           = useState("");
  const [editingMyName, setEditingMyName] = useState(false);
  const [tempName, setTempName]       = useState("");
  const [joinCode, setJoinCode]       = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]     = useState(false);
  const [friendBoxes, setFriendBoxes]     = useState([]);
  const [friendRequests, setFriendRequests] = useState([]); // incoming requests
  const [sentRequests, setSentRequests]     = useState([]); // outgoing pending
  const [showShareCard, setShowShareCard]   = useState(false);
  const [friendIdea, setFriendIdea]   = useState("");
  const [friendTarget, setFriendTarget] = useState(null);
  const [editingFriend, setEditingFriend] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [incoming, setIncoming]       = useState([]);
  const [toast, setToast]             = useState({ msg:"", visible:false });
  const [confetti, setConfetti]       = useState(false);
  const [totalDone, setTotalDone]     = useState(0);
  const [justLeveled, setJustLeveled] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [openCat, setOpenCat]         = useState(null);
  const [firstVisit, setFirstVisit]   = useState(false);
  const [myCreature, setMyCreature]   = useState("crab");
  const [labelIdx, setLabelIdx]       = useState(0);
  const [onboarding, setOnboarding]         = useState(null);
  const [onboardStep, setOnboardStep]       = useState(1);
  const [onboardUsernameError, setOnboardUsernameError] = useState("");
  const [onboardUsernameLoading, setOnboardUsernameLoading] = useState(false);
  const [showDone, setShowDone]       = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notifPrefs, setNotifPrefs]   = useState({ dailyNudge: false, streakReminder: false, friendTask: true, dueDateReminder: true, weeklySummary: false, nudgeHour: 9 });
  const [activeChest, setActiveChest] = useState("treasure");
  const [chestNames, setChestNames]   = useState({ treasure:"Treasure Chest", work:"Work Chest", creative:"Creative Chest" });
  const [editingChestName, setEditingChestName] = useState(null);
  const [tempChestName, setTempChestName] = useState("");
  const [chestEmojis, setChestEmojis]     = useState({ treasure:"🐚", work:"💼", creative:"✨" });
  const [tempChestEmoji, setTempChestEmoji] = useState("");
  const [crewTaskCounts, setCrewTaskCounts] = useState({});
  const [myHat, setMyHat]             = useState("none");
  const [editingIdea, setEditingIdea]     = useState(null);
  const [editingText, setEditingText]     = useState("");
  const [editingDue, setEditingDue]       = useState(null);
  const [editingDueInEdit, setEditingDueInEdit] = useState("");
  const [editingTimeInEdit, setEditingTimeInEdit] = useState("");
  const [focusTaskId, setFocusTaskId]   = useState(null);
  const [openMenu, setOpenMenu]         = useState(null);
  const [streak, setStreak]           = useState(0);
  const [lastActive, setLastActive]   = useState("");
  const [showMigration, setShowMigration] = useState(false);
  const [migrationCode, setMigrationCode] = useState("");
  const [migrating, setMigrating]     = useState(false);
  const [migrationError, setMigrationError] = useState("");
  const toastRef  = useRef();
  const notifRef  = useRef([]);

  const rotateLabel = () => setLabelIdx(i => (i+1) % CHEST_LABELS.length);
  const showToast = (msg) => {
    setToast({ msg, visible:true });
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(t=>({...t,visible:false})), 2600);
  };
  const fireCelebration = () => { setConfetti(true); setTimeout(()=>setConfetti(false),3500); };

  // Let module-scope helpers (saveProfile) raise the save-failure toast.
  useEffect(() => { setSaveErrorReporter(() => showToast("Couldn't save — check your connection 🌊")); }, []);

  // ── Auth listener ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (_event === "PASSWORD_RECOVERY") {
        setShowPasswordReset(true);
      }
      if (!session?.user) {
        setReady(false);
        setIdeas([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data when user logs in ──
  useEffect(() => {
    if (!user) return;
    (async () => {
      let profile = await loadProfile(user.id);
      const isNew = !profile;

      let code = profile?.code;
      if (!code) {
        code = await generateUniqueCode();
      }
      setMyCode(code);
      setMyName(profile?.username || "");
      setTotalDone(profile?.total_done || 0);
      setStreak(profile?.streak || 0);
      setLastActive(profile?.last_active || "");
      setMyCreature(profile?.creature || "crab");
      setMyHat(profile?.hat || "none");
      setFocusTaskId(profile?.focus_task_id || null);
      // Migrate old chest names to new Space names
      const rawNames = profile?.chest_names || {};
      const migratedNames = {
        treasure: (rawNames.treasure && rawNames.treasure !== "Treasure Chest") ? rawNames.treasure : "Life",
        work: (rawNames.work && rawNames.work !== "Work Chest") ? rawNames.work : "Work",
        creative: (rawNames.creative && rawNames.creative !== "Creative Chest") ? rawNames.creative : "Creative",
      };
      setChestNames(migratedNames);
      setChestEmojis(profile?.chest_emojis || { treasure:"🐚", work:"💼", creative:"✨" });
      setFriendBoxes(profile?.friends || []);
      setFriendRequests(profile?.friend_requests || []);
      setSentRequests(profile?.sent_requests || []);
      setNotifPrefs(profile?.notif_prefs || { dailyNudge: false, streakReminder: false, friendTask: true, nudgeHour: 9 });

      if (isNew) {
        setFirstVisit(true);
        setShowMigration(true); // ask if they have an old code first
        await saveProfile(user.id, {
          code,
          username: null,
          total_done: 0,
          streak: 0,
          last_active: "",
          creature: "crab",
          hat: "none",
          chest_names: { treasure:"Life", work:"Work", creative:"Creative" },
          chest_emojis: { treasure:"🐚", work:"💼", creative:"✨" },
          friends: [],
          notif_prefs: { dailyNudge: false, streakReminder: false, friendTask: true, nudgeHour: 9 },
          is_pro: false,
        });
      } else if (!profile?.creature) {
        setOnboarding("creature");
      }

      const savedIdeas = await loadIdeas(code);
      setIdeas(savedIdeas);

      const allIncoming = await loadIncoming(code);
      const requests = allIncoming.filter(i=>i.text==="__FRIEND_REQUEST__");
      const messages = allIncoming.filter(i=>i.text!=="__FRIEND_REQUEST__");
      setIncoming(messages);
      setFriendRequests(prev => {
        const fromDB = requests.map(r=>({ id:r.id, code:r.from_code, label:r.from_name }));
        return fromDB;
      });

      // Load crew task counts
      const friends = profile?.friends || [];
      if (friends.length > 0) {
        const counts = {};
        await Promise.all(friends.map(async f => {
          try {
            const { count } = await supabase.from("ideas").select("id", { count: "exact", head: true }).eq("client_id", f.code).eq("done", true);
            counts[f.code] = count || 0;
          } catch { counts[f.code] = 0; }
        }));
        setCrewTaskCounts(counts);
      }

      // Check streak
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now()-86400000).toDateString();
      const savedStreak = profile?.streak || 0;
      const la = profile?.last_active || "";
      if (la !== today && la !== yesterday && la !== "") {
        // streak broken — clear last_active too, else this re-runs every load
        await saveProfile(user.id, { streak: 0, last_active: "" });
        setStreak(0);
        setLastActive("");
      }

      setReady(true);
    })();
  }, [user]);

  // ── OneSignal init ──
  useEffect(() => {
    if (!ready) return;
    // Load OneSignal SDK
    if (!document.getElementById("onesignal-sdk")) {
      const script = document.createElement("script");
      script.id = "onesignal-sdk";
      script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
      script.defer = true;
      script.onload = () => initOneSignal();
      document.head.appendChild(script);
    } else {
      initOneSignal();
    }
  }, [ready]);

  // ── Notification tick ──
  useEffect(() => {
    if (!ready) return;
    const notify = (title, body) => {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body, icon: "/favicon.ico" });
      }
    };
    const tick = () => {
      const now = new Date();
      const h = now.getHours(), m = now.getMinutes();
      const todayKey = now.toDateString();
      if (notifPrefs.dailyNudge && h === notifPrefs.nudgeHour && m === 0) {
        const key = `nudge_${todayKey}`;
        if (!notifRef.current.includes(key)) {
          notifRef.current.push(key);
          notify("🌅 Morning nudge", "Time to check in! What will you tackle today?");
        }
      }
      if (notifPrefs.streakReminder && h === 20 && m === 0) {
        const key = `streak_${todayKey}`;
        if (lastActive !== todayKey && !notifRef.current.includes(key)) {
          notifRef.current.push(key);
          notify("🔥 Streak reminder", "Don't break your streak! Complete a task before midnight.");
        }
      }
      // Due date reminders — check at 9am
      if (h === 9 && m === 0) {
        const todayStr = new Date().toISOString().split("T")[0];
        const dueTodayKey = `due_${todayKey}`;
        if (!notifRef.current.includes(dueTodayKey)) {
          const dueToday = ideas.filter(i => !i.done && i.due_date === todayStr);
          if (dueToday.length > 0) {
            notifRef.current.push(dueTodayKey);
            notify("📅 Tasks due today", `You have ${dueToday.length} task${dueToday.length>1?"s":""} due today!`);
          }
        }
      }
      // Weekly summary — Sunday at 7pm
      if (new Date().getDay() === 0 && h === 19 && m === 0) {
        const weekKey = `week_${todayKey}`;
        if (!notifRef.current.includes(weekKey)) {
          notifRef.current.push(weekKey);
          const weekDone = ideas.filter(i => {
            if (!i.done) return false;
            const d = new Date(i.ts || i.created_at || 0);
            return d >= new Date(Date.now() - 7*86400000);
          }).length;
          notify("🌊 Weekly summary", `This week you completed ${weekDone} task${weekDone!==1?"s":""}. Keep sailing! 🦀`);
        }
      }
    };
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, [ready, notifPrefs, lastActive]);

  // ── Update streak ──
  const updateStreak = async () => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now()-86400000).toDateString();
    if (lastActive === today) return streak;
    const newStreak = lastActive === yesterday ? streak + 1 : 1;
    setStreak(newStreak);
    setLastActive(today);
    await saveProfile(user.id, { streak: newStreak, last_active: today });
    return newStreak;
  };

  // ── Chest unlock logic ──
  const crewCount = friendBoxes.length;
  const crewWith3Done = Object.values(crewTaskCounts).filter(n => n >= 3).length;
  const chestUnlocked = (chest) => {
    if (chest.unlockReq === 0) return true;
    if (chest.unlockReq === "pro") return false;
    return false;
  };

  const saveChestName = async (id, name, emoji) => {
    const updatedNames = { ...chestNames, [id]: name.trim() || CHESTS.find(c=>c.id===id).defaultName };
    const updatedEmojis = { ...chestEmojis, [id]: emoji || chestEmojis[id] };
    setChestNames(updatedNames);
    setChestEmojis(updatedEmojis);
    setEditingChestName(null);
    await saveProfile(user.id, { chest_names: updatedNames, chest_emojis: updatedEmojis });
    showToast("✏️ Chest updated!");
  };

  const saveNotifPrefs = async (updated) => {
    setNotifPrefs(updated);
    await saveProfile(user.id, { notif_prefs: updated });
  };

  const setDueDate = async (id, dateStr) => {
    const cleaned = cleanDate(dateStr) || null;
    setIdeas(prev => prev.map(i=>i.id===id?{...i,due_date:cleaned}:i));
    await supabase.from("ideas").update({ due_date: cleaned }).eq("id", id).eq("client_id", myCode);
    setEditingDue(null);
    showToast(dateStr ? "📅 Due date set!" : "Due date removed");
  };

  const saveUsername = async (name) => {
    const trimmed = name.trim().toLowerCase().replace(/[^a-z0-9_]/g,"");
    if (!trimmed) return;
    // Check uniqueness
    const { data: existing } = await supabase.from("user_profiles").select("id").eq("username", trimmed).neq("id", user.id).limit(1);
    if (existing && existing.length > 0) { showToast("Username taken — try another! 🦀"); return; }
    setMyName(trimmed);
    await saveProfile(user.id, { username: trimmed });
    await saveCodeDB(myCode, trimmed);
    setEditingMyName(false);
    showToast("Username saved! 🌊");
  };

  const handleOnboardUsername = async () => {
    const trimmed = tempName.trim().toLowerCase().replace(/[^a-z0-9_]/g,"");
    if (!trimmed) { setOnboardUsernameError("Please enter a username"); return; }
    if (trimmed.length < 3) { setOnboardUsernameError("Must be at least 3 characters"); return; }
    setOnboardUsernameLoading(true); setOnboardUsernameError("");
    const { data: existing } = await supabase.from("user_profiles").select("id").eq("username", trimmed).neq("id", user.id).limit(1);
    if (existing && existing.length > 0) { setOnboardUsernameError("Username taken — try another!"); setOnboardUsernameLoading(false); return; }
    setMyName(trimmed);
    await saveProfile(user.id, { username: trimmed });
    await saveCodeDB(myCode, trimmed);
    setOnboardUsernameLoading(false);
    setOnboardStep(3);
  };

  const parseNaturalDate = (text) => {
    if (!text.trim()) { setParsedDate(null); setParsedTime(null); setParsedText(""); return; }
    try {
      const results = chrono.parse(text, new Date(), { forwardDate: true });
      if (results.length > 0) {
        const result = results[0];
        const date = result.start.date();
        const dateStr = date.toISOString().split("T")[0];
        const timeStr = result.start.isCertain("hour")
          ? date.toLocaleTimeString("en", { hour:"2-digit", minute:"2-digit" })
          : null;
        // Clean text — remove the date phrase
        const cleaned = (text.slice(0, result.index) + text.slice(result.index + result.text.length)).trim().replace(/\s+/g," ").replace(/^[@,\s]+|[@,\s]+$/g,"").trim();
        setParsedDate(dateStr);
        setParsedTime(timeStr);
        setParsedText(cleaned || text.trim());
      } else {
        setParsedDate(null);
        setParsedTime(null);
        setParsedText("");
      }
    } catch(e) {
      setParsedDate(null); setParsedTime(null); setParsedText("");
    }
  };

  const addIdea = async () => {
    if (!newIdea.trim()) return;
    const taskText = parsedText || newIdea.trim();
    const newItem = {
      id: nextId(),
      text: taskText,
      done: false,
      ts: new Date().toLocaleDateString(),
      repeatable: false,
      chest_id: activeChest,
      due_date: parsedDate || null,
      due_time: parsedTime || null,
    };
    setIdeas(prev => [newItem, ...prev]);
    const { error } = await supabase.from("ideas").insert({ ...newItem, client_id: myCode });
    if (error) {
      setIdeas(prev => prev.filter(i => i.id !== newItem.id));
      showToast("Couldn't save — check your connection 🌊");
      return;
    }
    setNewIdea(""); setParsedDate(null); setParsedTime(null); setParsedText("");
    setFirstVisit(false);
    const toastMsg = parsedDate
      ? `✨ Added! Due ${parsedTime ? parsedTime + " on " : ""}${new Date(parsedDate+"T12:00:00").toLocaleDateString("en",{weekday:"short",month:"short",day:"numeric"})}`
      : "✨ Added to your chest!";
    showToast(toastMsg);
  };

  const toggleDone = async (id) => {
    const idea = ideas.find(i=>i.id===id);
    if (!idea) return;
    const wasUndone = !idea.done;
    if (wasUndone && idea.repeatable) {
      // Mark the current instance done (optimistic).
      setIdeas(prev => prev.map(i=>i.id===id?{...i,done:true}:i));
      // Build the fresh instance from the ORIGINAL task so its Space (chest_id),
      // due date and time carry over; drop the DB-managed created_at so the new
      // row gets its own timestamp.
      const { created_at, ...rest } = idea;
      const newItem = { ...rest, id: nextId(), done: false, ts: new Date().toLocaleDateString(), repeatable: true };
      // Persist immediately — do NOT wait for the animation. If the user closes
      // the tab during the delay, the DB is already consistent.
      const del = await supabase.from("ideas").delete().eq("id", id).eq("client_id", myCode);
      const ins = del.error ? { error: del.error } : await supabase.from("ideas").insert({ ...newItem, client_id: myCode });
      if (del.error || ins.error) {
        setIdeas(prev => prev.map(i=>i.id===id?{...i,done:false}:i));
        showToast("Couldn't save — check your connection 🌊");
        return;
      }
      // Visual delay only: swap the completed task for the fresh one after the beat.
      setTimeout(() => {
        setIdeas(prev => [newItem, ...prev.filter(i=>i.id!==id)]);
        showToast("🔁 Task reset and re-added!");
      }, 800);
    } else {
      setIdeas(prev => prev.map(i=>i.id===id?{...i,done:!i.done}:i));
      const { error } = await supabase.from("ideas").update({ done: !idea.done }).eq("id", id).eq("client_id", myCode);
      if (error) {
        setIdeas(prev => prev.map(i=>i.id===id?{...i,done:idea.done}:i));
        showToast("Couldn't save — check your connection 🌊");
        return;
      }
    }
    if (wasUndone) {
      const newStreak = await updateStreak();
      const newTotal = totalDone + 1;
      const prev = getStage(totalDone), next = getStage(newTotal);
      setTotalDone(newTotal);
      await saveProfile(user.id, { total_done: newTotal });
      fireCelebration();
      if (next.id > prev.id) { setJustLeveled(true); setTimeout(()=>setJustLeveled(false),3000); showToast(`🎉 ${next.emoji} ${next.name} unlocked!`); }
      else if (newStreak > 1) showToast(`🦀 Kani do it! 🔥 ${newStreak} day streak!`);
      else showToast("🦀 Kani do it! Task complete!");
    }
    if (shuffled?.id===id) setShuffled(null);
  };

  const deleteIdea = async (id) => {
    const prevIdeas = ideas;
    setIdeas(prev => prev.filter(i=>i.id!==id));
    const { error } = await supabase.from("ideas").delete().eq("id", id).eq("client_id", myCode);
    if (error) {
      setIdeas(prevIdeas);
      showToast("Couldn't save — check your connection 🌊");
      return;
    }
    if (shuffled?.id===id) setShuffled(null);
    if (editingIdea===id) setEditingIdea(null);
  };

  const saveEditIdea = async (id) => {
    if (!editingText.trim()) return;
    const updates = {
      text: editingText.trim(),
      due_date: editingDueInEdit || null,
      due_time: editingTimeInEdit || null,
    };
    setIdeas(prev => prev.map(i=>i.id===id?{...i,...updates}:i));
    await supabase.from("ideas").update(updates).eq("id", id).eq("client_id", myCode);
    setEditingIdea(null);
    showToast("✏️ Task updated!");
  };

  const toggleRepeatable = async (id) => {
    const idea = ideas.find(i=>i.id===id);
    const newVal = !idea?.repeatable;
    setIdeas(prev => prev.map(i=>i.id===id?{...i,repeatable:newVal}:i));
    await supabase.from("ideas").update({ repeatable: newVal }).eq("id", id).eq("client_id", myCode);
    showToast(newVal ? "🔁 Task set to repeat!" : "Task no longer repeating");
  };

  const addSuggestion = async (text) => {
    const newItem = { id: nextId(), text, done: false, ts: new Date().toLocaleDateString(), tag:"feel-good", repeatable: false, chest_id: activeChest };
    setIdeas(prev => [newItem, ...prev]);
    const { error } = await supabase.from("ideas").insert({ ...newItem, client_id: myCode });
    if (error) {
      setIdeas(prev => prev.filter(i => i.id !== newItem.id));
      showToast("Couldn't save — check your connection 🌊");
      return;
    }
    setShowSuggestions(false); setOpenCat(null);
    showToast("🌊 Added to your chest!");
  };

  const addSelectedSuggestions = async (tasks) => {
    const newItems = tasks.map(text => ({ id: nextId(), text, done: false, ts: new Date().toLocaleDateString(), tag:"feel-good", repeatable: false, chest_id: activeChest }));
    setIdeas(prev => [...newItems, ...prev]);
    await Promise.all(newItems.map(item => supabase.from("ideas").insert({ ...item, client_id: myCode })));
    setShowSuggestions(false); setOpenCat(null);
    showToast(`🌊 ${newItems.length} tasks added!`);
  };

  const doShuffle = () => {
    const pool = chestIdeas.filter(i=>!i.done);
    if (!pool.length) return;
    setShuffled(pool[Math.floor(Math.random()*pool.length)]);
  };

  const setFocusTask = async (id) => {
    const newFocus = focusTaskId === id ? null : id;
    setFocusTaskId(newFocus);
    await saveProfile(user.id, { focus_task_id: newFocus });
    showToast(newFocus ? "🎯 Focus set!" : "Focus cleared");
    setOpenMenu(null);
  };





  const doSearchFriend = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    const results = await searchByUsername(searchQuery.trim());
    setSearchResults(results.filter(r => r.code !== myCode && !friendBoxes.find(b=>b.code===r.code)));
    setSearching(false);
  };

  const joinFriend = async (code, label) => {
    if (!code) {
      code = (searchQuery || joinCode).trim().toUpperCase();
      if (code.length < 4) return;
    }
    if (code === myCode) { showToast("That's your own code!"); return; }
    if (friendBoxes.find(b=>b.code===code)) { showToast("Already friends!"); return; }
    if (sentRequests.find(r=>r.code===code)) { showToast("Request already sent!"); return; }
    if (!label) {
      const { data } = await supabase.from("user_profiles").select("code, username").eq("code", code).single().catch(()=>({data:null}));
      if (!data) {
        // try user_codes as fallback
        const { data: d2 } = await supabase.from("user_codes").select("code, username").eq("code", code).single().catch(()=>({data:null}));
        if (!d2) { showToast("Code not found — double check with your friend"); return; }
        label = d2.username || code;
      } else {
        label = data.username || code;
      }
    }
    // Send a friend request message
    await supabase.from("friend_messages").insert({
      to_code: code,
      from_code: myCode,
      from_name: myName || myCode,
      text: `__FRIEND_REQUEST__`,
      ts: new Date().toLocaleDateString()
    });
    const updatedSent = [...sentRequests, { code, label }];
    setSentRequests(updatedSent);
    await saveProfile(user.id, { sent_requests: updatedSent });
    setJoinCode(""); setSearchQuery(""); setSearchResults([]);
    showToast(`Friend request sent to ${label}! 🌊`);
  };

  const removeFriend = async (code) => {
    const updated = friendBoxes.filter(b=>b.code!==code);
    setFriendBoxes(updated);
    await saveProfile(user.id, { friends: updated });
    if (friendTarget===code) setFriendTarget(null);
    showToast("Friend removed");
  };

  const acceptFriendRequest = async (req) => {
    // Add to friends
    const updated = [...friendBoxes, { code: req.code, label: req.label }];
    setFriendBoxes(updated);
    // Remove from requests
    const updatedReqs = friendRequests.filter(r=>r.code!==req.code);
    setFriendRequests(updatedReqs);
    // Delete the request message
    if (req.id) await deleteMessage(req.id);
    await saveProfile(user.id, { friends: updated });
    // Send them a message so they know we accepted
    await supabase.from("friend_messages").insert({
      to_code: req.code,
      from_code: myCode,
      from_name: myName || myCode,
      text: `__FRIEND_ACCEPTED__`,
      ts: new Date().toLocaleDateString()
    });
    showToast(`You and ${req.label} are now friends! 🌊`);
  };

  const declineFriendRequest = async (req) => {
    const updatedReqs = friendRequests.filter(r=>r.code!==req.code);
    setFriendRequests(updatedReqs);
    if (req.id) await deleteMessage(req.id);
    showToast("Request declined");
  };

  const sendToFriend = async (code) => {
    if (!friendIdea.trim()) return;
    await sendMessage(code, myCode, myName, friendIdea.trim());
    setFriendIdea(""); setFriendTarget(null);
    showToast("💌 Sent!");
  };

  const acceptIncoming = async (item) => {
    const newItem = { id: nextId(), text: item.text, done: false, ts: item.ts, from_friend: item.from_name || item.from_code, repeatable: false };
    setIdeas(prev => [newItem, ...prev]);
    const { error } = await supabase.from("ideas").insert({ ...newItem, client_id: myCode });
    if (error) {
      setIdeas(prev => prev.filter(i => i.id !== newItem.id));
      showToast("Couldn't save — check your connection 🌊");
      return;
    }
    await deleteMessage(item.id);
    setIncoming(prev => prev.filter(i=>i.id!==item.id));
    showToast("✨ Added to your chest!");
  };

  const dismissIncoming = async (item) => {
    await deleteMessage(item.id);
    setIncoming(prev => prev.filter(i=>i.id!==item.id));
  };

  const pickCreature = async (id) => {
    setMyCreature(id);
    await saveProfile(user.id, { creature: id });
  };

  const renameFriend = async (code, newLabel) => {
    const updated = friendBoxes.map(b=>b.code===code?{...b,label:newLabel}:b);
    setFriendBoxes(updated);
    await saveProfile(user.id, { friends: updated });
    setEditingFriend(null); setEditingName("");
    showToast("Name updated! ⚓");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    showToast("Logged out 👋");
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token || ""}` },
      });
      if (!res.ok) throw new Error();
      await supabase.auth.signOut();
      window.location.reload();
    } catch {
      setDeleting(false);
      showToast("Couldn't delete account — try again 🦀");
    }
  };

  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSendingFeedback(true);
    const { error } = await supabase.from("feedback").insert({
      message: feedbackText.trim(),
      user_id: user?.id || null,
      code: myCode || null,
      app_version: APP_VERSION,
      user_agent: navigator.userAgent,
    });
    setSendingFeedback(false);
    if (error) { showToast("Couldn't send — check your connection 🌊"); return; }
    setFeedbackText(""); setFeedbackSent(true);
  };

  // ── Relink old code ──
  const [showRelinkCode, setShowRelinkCode] = useState(false);
  const [relinkCode, setRelinkCode]         = useState("");
  const [relinkLoading, setRelinkLoading]   = useState(false);
  const [relinkError, setRelinkError]       = useState("");

  const handleRelink = async () => {
    const oldCode = relinkCode.trim().toUpperCase();
    if (oldCode.length < 4) { setRelinkError("Please enter a 4-letter code"); return; }
    if (oldCode === myCode) { setRelinkError("That's already your current code!"); return; }
    setRelinkLoading(true); setRelinkError("");
    try {
      // Search ideas directly — no longer requires user_codes entry
      const { data: oldIdeas } = await supabase.from("ideas").select("*").eq("client_id", oldCode);

      // Also check friend_messages as fallback signal the code existed
      const { data: msgs } = await supabase.from("friend_messages").select("id").eq("to_code", oldCode).limit(1);

      if ((!oldIdeas || oldIdeas.length === 0) && (!msgs || msgs.length === 0)) {
        setRelinkError("No data found for that code — double check it!");
        setRelinkLoading(false); return;
      }

      if (oldIdeas && oldIdeas.length > 0) {
        await Promise.all(oldIdeas.map(idea =>
          supabase.from("ideas").update({ client_id: myCode }).eq("id", idea.id)
        ));
        setIdeas(prev => {
          const existingIds = new Set(prev.map(i=>i.id));
          const newOnes = oldIdeas.filter(i=>!existingIds.has(i.id)).map(i=>({...i,client_id:myCode}));
          return [...newOnes, ...prev];
        });
      }

      const completedCount = (oldIdeas||[]).filter(i=>i.done).length;
      const newTotal = Math.max(completedCount, totalDone);
      await saveProfile(user.id, { total_done: newTotal });
      setTotalDone(newTotal);

      // Clean up old code if it exists
      await supabase.from("user_codes").delete().eq("code", oldCode);
      setShowRelinkCode(false); setRelinkCode("");
      setShowSettings(false);
      showToast("🎉 Old data recovered successfully!");
    } catch(e) {
      setRelinkError("Something went wrong — try again");
    }
    setRelinkLoading(false);
  };

  const handleMigration = async () => {
    const oldCode = migrationCode.trim().toUpperCase();
    if (oldCode.length < 4) { setMigrationError("Please enter your 4-letter code"); return; }
    setMigrating(true); setMigrationError("");
    try {
      // Search ideas directly — no longer requires user_codes entry
      const { data: oldIdeas } = await supabase.from("ideas").select("*").eq("client_id", oldCode);
      const { data: msgs } = await supabase.from("friend_messages").select("id").eq("to_code", oldCode).limit(1);

      if ((!oldIdeas || oldIdeas.length === 0) && (!msgs || msgs.length === 0)) {
        setMigrationError("No data found for that code — double check it!");
        setMigrating(false); return;
      }

      const newCode = myCode;
      if (oldIdeas && oldIdeas.length > 0) {
        await Promise.all(oldIdeas.map(idea =>
          supabase.from("ideas").update({ client_id: newCode }).eq("id", idea.id)
        ));
        setIdeas(oldIdeas.map(i => ({ ...i, client_id: newCode })));
      }

      const completedCount = (oldIdeas || []).filter(i => i.done).length;
      const newTotal = Math.max(completedCount, totalDone);

      await saveProfile(user.id, { total_done: newTotal });
      setTotalDone(newTotal);

      // Clean up old code if exists
      await supabase.from("user_codes").delete().eq("code", oldCode);

      setShowMigration(false);
      setOnboarding("creature");
      showToast("🎉 Your data has been recovered!");
    } catch (e) {
      setMigrationError("Something went wrong — try again");
    }
    setMigrating(false);
  };

  // ── Derived state ──
  const stage     = getStage(totalDone);
  const nextStage = getNextStage(totalDone);
  const toNext    = nextStage ? nextStage.req - totalDone : 0;
  const stagePct  = nextStage ? ((totalDone-stage.req)/(nextStage.req-stage.req))*100 : 100;
  const chestIdeas = ideas.filter(i => (i.chest_id || "treasure") === activeChest);
  const activeCnt = chestIdeas.filter(i=>!i.done).length;
  const doneCnt   = chestIdeas.filter(i=>i.done).length;
  const justSmiled = doneCnt >= 1;

  const C = { aqua:"#B2EBF2", bgFrom:"#4DB6AC", bgTo:"#006064", tealDark:"#00695C", coral:"#FF8A71", navText:"#002D2F", muted:"#4a8c8e", card:"#ffffff", cardBorder:"#C8F0F5", accent2:"#00897B", gold:"#FFB300", danger:"#E53935", inputBg:"#E0F7FA", inputBorder:"#80DEEA" };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Nunito',sans-serif;background:${C.bgTo};-webkit-tap-highlight-color:transparent}
    button{cursor:pointer;font-family:'Nunito',sans-serif}
    textarea,input{font-family:'Nunito',sans-serif}
    @keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    @keyframes bobFast{0%,100%{transform:translateY(0)}50%{transform:translateY(-11px)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
    @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes popIn{0%{transform:scale(.87);opacity:0}70%{transform:scale(1.05)}100%{transform:scale(1);opacity:1}}
    .idea-row{animation:slideUp .26s ease both}
    .tab-pane{animation:fadeIn .22s ease}
  `;

  const s = {
    app:      { minHeight:"100vh", background:`linear-gradient(175deg,${C.bgFrom} 0%,${C.bgTo} 100%)`, color:C.navText, paddingBottom:100 },
    headerBg: { background:C.tealDark, width:"100%" },
    wrap:     { maxWidth:480, margin:"0 auto", padding:"20px 16px 0" },
    topBand:  { background:C.aqua },
    header:   { padding:"26px 20px 0", textAlign:"center" },
    logo:     { fontSize:38, fontWeight:700, color:C.navText, fontFamily:"'Fredoka',sans-serif", letterSpacing:.5 },
    logoC:    { color:C.coral },
    tagline:  { fontSize:11.5, color:C.muted, letterSpacing:2.5, textTransform:"uppercase", marginTop:5, marginBottom:18, fontWeight:700 },
    tabs:     { display:"flex", gap:7, padding:"10px 14px 14px" },
    tab:      (a) => ({ flex:1, padding:"10px 3px", borderRadius:14, border:"none", fontSize:12, fontWeight:800, transition:"all .18s", background:a?C.coral:"rgba(0,45,47,0.1)", color:a?"#fff":C.navText, boxShadow:a?"0 3px 10px rgba(255,138,113,0.45)":"none", letterSpacing:.2 }),
    card:     { background:C.card, border:`1px solid ${C.cardBorder}`, borderRadius:20, padding:20, marginBottom:14, boxShadow:"0 2px 20px rgba(0,96,100,0.10)" },
    advCard:  { background:C.aqua, borderRadius:20, padding:20, marginBottom:14, boxShadow:"0 2px 20px rgba(0,96,100,0.12)" },
    label:    { fontSize:11, color:C.muted, letterSpacing:2.5, textTransform:"uppercase", marginBottom:12, fontWeight:800, display:"block" },
    item:     (d) => ({ background:d?"#F1FBF6":"#fff", border:`1.5px solid ${d?"#B2DFDB":C.cardBorder}`, borderRadius:16, padding:"13px 14px", display:"flex", alignItems:"flex-start", gap:12, marginBottom:9 }),
    check:    (d) => ({ width:28, height:28, borderRadius:"50%", border:`2.5px solid ${d?C.accent2:C.inputBorder}`, background:d?C.accent2:"transparent", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:13, fontWeight:900, marginTop:1, transition:"all .2s", cursor:"pointer" }),
    iText:    (d) => ({ flex:1, fontSize:14.5, lineHeight:1.55, color:d?C.muted:C.navText, textDecoration:d?"line-through":"none", textDecorationColor:C.accent2 }),
    del:      { background:"transparent", border:"none", color:"#aaa", fontSize:16, padding:"0 2px", flexShrink:0, marginTop:2 },
    meta:     { fontSize:11, color:C.muted, marginTop:3, opacity:.8 },
    textarea: { width:"100%", background:C.inputBg, border:`1.5px solid ${C.inputBorder}`, borderRadius:14, padding:"13px 15px", color:C.navText, fontSize:15, resize:"none", minHeight:92, outline:"none", lineHeight:1.55 },
    input:    { flex:1, background:C.inputBg, border:`1.5px solid ${C.inputBorder}`, borderRadius:13, padding:"12px 15px", color:C.navText, fontSize:14, outline:"none" },
    inputCode:{ flex:1, background:C.inputBg, border:`1.5px solid ${C.inputBorder}`, borderRadius:13, padding:"12px 15px", color:C.navText, fontSize:14, outline:"none", letterSpacing:3, textTransform:"uppercase" },
    btn:      (bg=C.coral,fg="#fff") => ({ width:"100%", marginTop:11, padding:"14px", background:bg, border:"none", borderRadius:14, color:fg, fontSize:15, fontWeight:800, transition:"opacity .15s", boxShadow:`0 3px 10px ${bg}44`, letterSpacing:.3 }),
    smBtn:    (c) => ({ padding:"6px 13px", background:c+"18", border:`1.5px solid ${c}55`, borderRadius:9, color:c, fontSize:12, fontWeight:800 }),
    outline:  { background:"transparent", border:`1.5px solid ${C.inputBorder}`, borderRadius:13, padding:"11px 15px", color:C.navText, fontSize:13, fontWeight:700 },
    row:      { display:"flex", gap:8 },
    empty:    { textAlign:"center", padding:"44px 24px", color:"rgba(255,255,255,0.75)", fontSize:14, lineHeight:1.9 },
    toast:    (v) => ({ position:"fixed", bottom:28, left:"50%", transform:`translateX(-50%) translateY(${v?0:10}px)`, background:"#002D2F", color:"#fff", padding:"12px 24px", borderRadius:50, fontSize:13, fontWeight:800, zIndex:400, pointerEvents:"none", whiteSpace:"nowrap", transition:"all .3s", opacity:v?1:0, boxShadow:"0 4px 20px rgba(0,0,0,0.25)" }),
    codeVal:  { fontFamily:"monospace", fontSize:30, letterSpacing:7, color:C.coral, fontWeight:900 },
    divider:  { textAlign:"center", color:"rgba(255,255,255,0.45)", fontSize:12, margin:"6px 0 16px", letterSpacing:1.5, fontWeight:600 },
  };

  // ── Auth not yet checked ──
  if (!authChecked) return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,#4DB6AC,#006064)`, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ animation:"bob 1.4s infinite" }}><Crab size={72}/></div>
      <style>{`@keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}`}</style>
      <div style={{ color:"#B2EBF2", fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:600 }}>Loading...</div>
    </div>
  );

  // ── Password reset screen ──
  if (showPasswordReset) return <PasswordResetScreen onDone={()=>setShowPasswordReset(false)}/>;

  // ── Not logged in ──
  if (!user) return <LoginScreen onLogin={() => {}} />;

  // ── Loading data ──
  if (!ready) return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,#4DB6AC,#006064)`, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <style>{css}</style>
      <div style={{ animation:"bob 1.4s infinite" }}><Crab size={72}/></div>
      <div style={{ color:"#B2EBF2", fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:600 }}>Loading Kani Do...</div>
    </div>
  );

  // ── Migration screen ──
  if (showMigration) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,${C.bgFrom},${C.bgTo})`, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:"#fff", borderRadius:28, padding:"36px 28px", maxWidth:360, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.25)", textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🐚</div>
            <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:24, fontWeight:700, color:C.navText, marginBottom:8 }}>
              Welcome back?
            </div>
            <div style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:24 }}>
              If you used Kani Do before, enter your old 4-letter code to recover your tasks and progress!
            </div>

            <input
              placeholder="Your old code (e.g. A3X9)"
              value={migrationCode}
              onChange={e=>{ setMigrationCode(e.target.value.toUpperCase()); setMigrationError(""); }}
              maxLength={4}
              style={{ width:"100%", background:C.inputBg, border:`1.5px solid ${migrationError?C.coral:C.inputBorder}`, borderRadius:14, padding:"14px 16px", color:C.navText, fontSize:22, outline:"none", marginBottom:10, fontFamily:"'Fredoka',sans-serif", letterSpacing:8, textAlign:"center", fontWeight:700 }}
            />
            {migrationError && <div style={{ color:C.coral, fontSize:12, fontWeight:700, marginBottom:10 }}>{migrationError}</div>}

            <button onClick={handleMigration} disabled={migrating}
              style={{ width:"100%", padding:"14px", background:`linear-gradient(130deg,${C.accent2},#26C6DA)`, border:"none", borderRadius:14, color:"#fff", fontSize:15, fontWeight:900, cursor:"pointer", marginBottom:12, opacity:migrating?0.7:1, fontFamily:"'Nunito',sans-serif", boxShadow:"0 4px 16px rgba(0,137,123,0.35)" }}>
              {migrating ? "Recovering..." : "🌊 Recover my data"}
            </button>

            <button onClick={()=>{ setShowMigration(false); setOnboarding("creature"); }}
              style={{ width:"100%", padding:"13px", background:"transparent", border:`1.5px solid ${C.inputBorder}`, borderRadius:14, color:C.muted, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
              I'm new here — start fresh! 🦀
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Onboarding ──
  if (onboarding === "creature") {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh", background:`linear-gradient(175deg,${C.bgFrom},${C.bgTo})`, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ maxWidth:400, width:"100%" }}>
            {onboardStep === 1 && (
              <div style={{ background:"#fff", borderRadius:28, padding:"32px 24px", textAlign:"center", animation:"popIn .4s ease" }}>
                <div style={{ marginBottom:16, animation:"bob 2s infinite" }}><Crab size={80} smiling={true}/></div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:26, fontWeight:700, color:C.navText, marginBottom:8 }}>Welcome to Kani Do! 🦀</div>
                <div style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:24 }}>First things first — pick your companion for the journey!</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:20 }}>
                  {CREATURES.filter(c=>c.unlockReq===0).map(c => (
                    <button key={c.id} onClick={async ()=>{ await pickCreature(c.id); setOnboardStep(2); }}
                      style={{ background:myCreature===c.id?C.coral+"18":"rgba(0,45,47,0.04)", border:`2px solid ${myCreature===c.id?C.coral:C.inputBorder}`, borderRadius:16, padding:"14px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:8, cursor:"pointer", transition:"all .2s" }}>
                      <CreatureIcon id={c.id} size={44}/>
                      <span style={{ fontSize:11, fontWeight:800, color:myCreature===c.id?C.coral:C.navText }}>{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {onboardStep === 2 && (
              <div style={{ background:"#fff", borderRadius:28, padding:"32px 24px", textAlign:"center", animation:"popIn .4s ease" }}>
                <div style={{ marginBottom:16, animation:"bob 2s infinite" }}><CreatureIcon id={myCreature} size={80}/></div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:24, fontWeight:700, color:C.navText, marginBottom:8 }}>What should we call you? 🌊</div>
                <div style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:20 }}>
                  Pick a unique username so your crew can find you! You can always change it later.
                </div>
                <input
                  placeholder="e.g. sarineh, kanimaster, oceanpro..."
                  value={tempName}
                  onChange={e=>{ setTempName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"")); setOnboardUsernameError(""); }}
                  maxLength={20}
                  style={{ width:"100%", background:C.inputBg, border:`1.5px solid ${onboardUsernameError?C.coral:C.inputBorder}`, borderRadius:14, padding:"13px 16px", color:C.navText, fontSize:15, outline:"none", marginBottom:8, fontFamily:"'Nunito',sans-serif", textAlign:"center", fontWeight:700 }}
                  onKeyDown={e=>e.key==="Enter"&&handleOnboardUsername()}
                />
                {onboardUsernameError && <div style={{ color:C.coral, fontSize:12, fontWeight:700, marginBottom:8 }}>{onboardUsernameError}</div>}
                <div style={{ fontSize:11, color:C.muted, marginBottom:16 }}>Lowercase letters, numbers and _ only</div>
                <button onClick={handleOnboardUsername} disabled={onboardUsernameLoading||!tempName.trim()}
                  style={{ ...s.btn(C.coral), marginTop:0, fontSize:15, opacity:onboardUsernameLoading||!tempName.trim()?0.6:1 }}>
                  {onboardUsernameLoading ? "Checking..." : "Claim my username →"}
                </button>

              </div>
            )}
            {onboardStep === 3 && (
              <div style={{ background:"#fff", borderRadius:28, padding:"32px 24px", textAlign:"center", animation:"popIn .4s ease" }}>
                <div style={{ marginBottom:16, animation:"bob 2s infinite" }}><CreatureIcon id={myCreature} size={80}/></div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:24, fontWeight:700, color:C.navText, marginBottom:8 }}>
                  {myName ? `Welcome, ${myName}! 🎉` : "You're all set! 🎉"}
                </div>
                <div style={{ fontSize:14, color:C.muted, lineHeight:1.7, marginBottom:20 }}>Here's what Kani Do is all about:</div>
                {[
                  { emoji:"🐚", title:"Your space", desc:"Add tasks and ideas. Tick them off to explore further." },
                  { emoji:"🗺️", title:"Your progress", desc:"See your ocean journey, streaks, and how far you've come." },
                  { emoji:"🌊", title:"Your friends", desc:"Connect with people you trust and support each other." },
                ].map(item => (
                  <div key={item.title} style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:16, textAlign:"left", background:C.inputBg, borderRadius:14, padding:"12px 16px" }}>
                    <span style={{ fontSize:28 }}>{item.emoji}</span>
                    <div>
                      <div style={{ color:C.navText, fontWeight:800, fontSize:15, marginBottom:3 }}>{item.title}</div>
                      <div style={{ color:C.muted, fontSize:13, lineHeight:1.5 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
                <button onClick={()=>setOnboarding(null)} style={{ ...s.btn(C.coral), marginTop:8, fontSize:16 }}>
                  Let's go! 🦀
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Main UI ──
  return (
    <>
      <style>{css}</style>
      <Confetti active={confetti}/>
      <div style={s.app}>
        {/* ── PROFILE STRIP (Tasks tab only) ── */}
        {tab==="chest" && (
          <div style={{ background:C.tealDark, width:"100%", paddingTop:"env(safe-area-inset-top)" }}>
          <div style={{ padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", maxWidth:480, margin:"0 auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <CreatureWithAccessory id={myCreature} size={36} hatId={myHat} smiling={justSmiled}/>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{myName || "Hey there"} 👋</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)" }}>What's one thing you want to do today?</div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              {streak > 0 && <div style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(255,255,255,0.12)", borderRadius:50, padding:"4px 10px" }}>
                <span style={{ fontSize:13 }}>🔥</span>
                <span style={{ fontSize:12, fontWeight:800, color:"#fff" }}>{streak}</span>
              </div>}
              <div style={{ display:"flex", alignItems:"center", gap:4, background:"rgba(255,255,255,0.12)", borderRadius:50, padding:"4px 10px" }}>
                <span style={{ fontSize:13 }}>⭐</span>
                <span style={{ fontSize:12, fontWeight:800, color:"#fff" }}>{totalDone}</span>
              </div>
              <button onClick={()=>setShowSettings(true)} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:50, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16 }}>⚙️</button>
            </div>
          </div>
          </div>
        )}

        {/* ── PROGRESS BAR (Tasks tab only) ── */}
        {tab==="chest" && nextStage && (
          <div style={{ background:C.tealDark, width:"100%" }}>
          <div style={{ padding:"0 20px 10px", maxWidth:480, margin:"0 auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"rgba(255,255,255,0.6)", marginBottom:4, fontWeight:700 }}>
              <span>{stage.emoji} {stage.name}</span>
              <span>{toNext} task{toNext!==1?"s":""} to {nextStage.emoji} {nextStage.name}</span>
            </div>
            <div style={{ height:5, background:"rgba(255,255,255,0.15)", borderRadius:50, overflow:"hidden" }}>
              <div style={{ height:"100%", width:Math.max(4,stagePct)+"%", background:`linear-gradient(90deg,${C.coral},${C.gold})`, borderRadius:50, transition:"width 1s ease" }}/>
            </div>
          </div>
          </div>
        )}

        <svg viewBox="0 0 480 28" preserveAspectRatio="none" style={{ display:"block", width:"100%" }} height="20">
          <path d="M0,0 L480,0 L480,6 Q360,20 240,12 Q120,4 0,14 Z" fill={C.tealDark}/>
        </svg>

        <div style={s.wrap} className="tab-pane" key={tab}>

          {/* ── MAP ── */}
          {tab==="map" && (
            <>
              {/* Today's focus card */}
              {focusTaskId && ideas.find(i=>i.id===focusTaskId&&!i.done) ? (
                <div style={{ background:`linear-gradient(130deg,${C.accent2},#26C6DA)`, borderRadius:20, padding:"18px 20px", marginBottom:14, boxShadow:"0 4px 20px rgba(0,137,123,0.3)" }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.75)", letterSpacing:2, textTransform:"uppercase", fontWeight:800, marginBottom:8 }}>🎯 Today's focus</div>
                  <div style={{ fontSize:16, fontWeight:800, color:"#fff", lineHeight:1.5, marginBottom:12 }}>{ideas.find(i=>i.id===focusTaskId)?.text}</div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={()=>toggleDone(focusTaskId)} style={{ flex:1, padding:"10px", background:"rgba(255,255,255,0.25)", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>Done!</button>
                    <button onClick={()=>setFocusTask(null)} style={{ padding:"10px 14px", background:"rgba(255,255,255,0.15)", border:"none", borderRadius:12, color:"#fff", fontSize:13, cursor:"pointer" }}>Clear</button>
                  </div>
                </div>
              ) : (
                <div style={{ background:"rgba(255,255,255,0.1)", border:"1.5px dashed rgba(255,255,255,0.3)", borderRadius:16, padding:"14px 16px", marginBottom:14, textAlign:"center" }}>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>No focus set for today</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:4 }}>Tap ··· on any task to set it as your focus</div>
                </div>
              )}

              <div style={{ ...s.advCard, textAlign:"center" }}>
                <div style={{ animation:justLeveled?"bobFast .4s 4":"bob 2.2s infinite ease-in-out", display:"inline-flex", justifyContent:"center", marginBottom:8 }}>
                  <CreatureWithAccessory id={myCreature} size={84} animate={justLeveled} smiling={justSmiled} hatId={myHat}/>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:12, flexWrap:"wrap", justifyContent:"center" }}>
                  {ISLAND_BADGES.map(b => {
                    const earned = totalDone >= STAGES[b.stageId].req;
                    return (
                      <div key={b.stageId} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                        <span style={{ fontSize:16, opacity:earned?1:0.22, filter:earned&&stage.id===b.stageId?"drop-shadow(0 0 5px #FFB300)":"none", transition:"all .3s" }} title={earned ? b.label : `🔒 ${b.label}`}>
                          {earned ? b.badge : "🔒"}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:700, fontSize:22, color:C.navText, marginBottom:3 }}>{stage.emoji} {stage.name}</div>
                <div style={{ fontSize:13, color:C.muted, marginBottom:12 }}>{stage.desc}</div>
                <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(0,45,47,0.08)", borderRadius:50, padding:"6px 16px", marginBottom:8 }}>
                  <span style={{ fontSize:16 }}>⭐</span>
                  <span style={{ fontSize:14, fontWeight:800, color:C.navText }}>{totalDone} task{totalDone!==1?"s":""} completed</span>
                </div>
                {streak > 0 && (
                  <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:streak>=7?"rgba(255,179,0,0.18)":"rgba(255,138,113,0.15)", borderRadius:50, padding:"6px 16px", marginBottom:8, marginLeft:8 }}>
                    <span style={{ fontSize:16 }}>🔥</span>
                    <span style={{ fontSize:14, fontWeight:800, color:streak>=7?C.gold:C.coral }}>{streak} day{streak!==1?"s":""} streak{streak>=7?" 🏆":streak>=3?" 💪":"!"}</span>
                  </div>
                )}
                {nextStage ? (
                  <div style={{ marginTop:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:C.muted, marginBottom:6, fontWeight:700 }}>
                      <span>Next: {nextStage.emoji} {nextStage.name}</span><span>{toNext} to go</span>
                    </div>
                    <div style={{ height:10, background:"rgba(0,45,47,0.12)", borderRadius:50, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:Math.max(4,stagePct)+"%", background:`linear-gradient(90deg,${C.coral},${C.gold})`, borderRadius:50, transition:"width 1s ease" }}/>
                    </div>
                  </div>
                ) : <div style={{ fontSize:14, color:C.gold, fontWeight:800, marginTop:8 }}>🏆 You've reached the end — legend!</div>}

                {/* Share button */}
                <button onClick={()=>setShowShareCard(true)}
                  style={{ marginTop:14, width:"100%", padding:"11px", background:`linear-gradient(130deg,${C.coral},${C.gold})`, border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Nunito',sans-serif" }}>
                  Share my progress 🌊
                </button>
              </div>

              <div style={s.card}>
                <span style={s.label}>Adventure Map</span>
                {STAGES.map((st,i) => {
                  const unlocked = totalDone>=st.req, current = st.id===stage.id;
                  const badge = ISLAND_BADGES.find(b=>b.stageId===st.id);
                  return (
                    <div key={st.id} style={{ display:"flex", alignItems:"center", gap:13, padding:"11px 0", borderBottom:i<STAGES.length-1?`1px solid ${C.cardBorder}`:"none" }}>
                      <div style={{ width:28, height:28, borderRadius:"50%", background:current?C.coral:unlocked?C.accent2+"28":"#E0F7FA", border:`2.5px solid ${current?C.coral:unlocked?C.accent2:C.inputBorder}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:900, color:current?"#fff":unlocked?C.accent2:C.muted, flexShrink:0, animation:current?"pulse 1.8s infinite":"none" }}>
                        {current?"🦀":unlocked?"✓":"🔒"}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:800, fontSize:14, color:unlocked?C.navText:C.muted }}>{st.emoji} {st.name}</div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{st.req===0?"Starting point":`Unlocks at ${st.req} tasks`}</div>
                      </div>
                      {unlocked && badge && (
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                          <span style={{ fontSize:20, filter: current?"drop-shadow(0 0 6px #FFB300)":"none", animation:current?"pulse 2s infinite":"none" }}>{badge.badge}</span>
                          <span style={{ fontSize:9, color:C.gold, fontWeight:800, whiteSpace:"nowrap" }}>{badge.label}</span>
                        </div>
                      )}
                      {!unlocked && badge && (
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, opacity:.3 }}>
                          <span style={{ fontSize:20 }}>🔒</span>
                          <span style={{ fontSize:9, color:C.muted, fontWeight:700, whiteSpace:"nowrap" }}>{badge.label}</span>
                        </div>
                      )}
                      {current && <div style={{ fontSize:11, background:C.coral+"20", color:C.coral, padding:"4px 12px", borderRadius:50, fontWeight:800, whiteSpace:"nowrap" }}>You are here!</div>}
                    </div>
                  );
                })}

              </div>

              {/* ── YOUR JOURNEY ── */}
              <div style={{ ...s.card, marginTop:0 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <span style={{ fontFamily:"'Fredoka',sans-serif", fontSize:20, fontWeight:700, color:C.navText }}>🌊 Your Journey</span>
                </div>

                {/* Key stats row */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                  {[
                    { label:"Completed", value:totalDone, emoji:"✅" },
                    { label:"Streak", value:`${streak}d`, emoji:"🔥" },
                    { label:"This week", value:ideas.filter(i=>{ const d=new Date(i.ts||i.created_at||Date.now()); return i.done && d >= new Date(Date.now()-7*86400000); }).length, emoji:"📅" },
                  ].map((stat,i) => (
                    <div key={i} style={{ background:C.inputBg, borderRadius:14, padding:"12px 8px", textAlign:"center" }}>
                      <div style={{ fontSize:20, marginBottom:4 }}>{stat.emoji}</div>
                      <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:700, color:C.navText }}>{stat.value}</div>
                      <div style={{ fontSize:10, color:C.muted, fontWeight:700 }}>{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Daily bar chart - last 7 days */}
                {(() => {
                  const days = Array.from({length:7},(_,i)=>{
                    const d = new Date(Date.now()-(6-i)*86400000);
                    const label = d.toLocaleDateString("en",{weekday:"short"});
                    const count = ideas.filter(idea=>{
                      if(!idea.done) return false;
                      const td = new Date(idea.ts||idea.created_at||0);
                      return td.toDateString()===d.toDateString();
                    }).length;
                    return { label, count };
                  });
                  const maxCount = Math.max(...days.map(d=>d.count), 1);
                  return (
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontSize:11, color:C.muted, letterSpacing:2, textTransform:"uppercase", fontWeight:800, marginBottom:10 }}>Tasks completed per day</div>
                      <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:80 }}>
                        {days.map((d,i)=>(
                          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4, height:"100%" }}>
                            <div style={{ flex:1, width:"100%", display:"flex", alignItems:"flex-end" }}>
                              <div style={{ width:"100%", height:`${Math.max(8,(d.count/maxCount)*100)}%`, background:d.count>0?`linear-gradient(180deg,${C.coral},${C.accent2})`:"rgba(0,45,47,0.08)", borderRadius:"6px 6px 0 0", transition:"height .5s ease", minHeight:8 }}/>
                            </div>
                            <div style={{ fontSize:9, color:C.muted, fontWeight:700 }}>{d.label}</div>
                            {d.count>0 && <div style={{ fontSize:9, color:C.coral, fontWeight:900 }}>{d.count}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Average + best day */}
                {(() => {
                  const last7 = ideas.filter(i=>{
                    if(!i.done) return false;
                    const d = new Date(i.ts||i.created_at||0);
                    return d >= new Date(Date.now()-7*86400000);
                  });
                  const avg = (last7.length / 7).toFixed(1);
                  const byDay = {};
                  last7.forEach(i=>{
                    const d = new Date(i.ts||i.created_at||0).toLocaleDateString("en",{weekday:"long"});
                    byDay[d] = (byDay[d]||0)+1;
                  });
                  const bestDay = Object.entries(byDay).sort((a,b)=>b[1]-a[1])[0];
                  const completionRate = ideas.length > 0 ? Math.round((ideas.filter(i=>i.done).length/ideas.length)*100) : 0;
                  return (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                      <div style={{ background:C.inputBg, borderRadius:12, padding:"12px 14px" }}>
                        <div style={{ fontSize:10, color:C.muted, fontWeight:800, marginBottom:4 }}>AVG PER DAY</div>
                        <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:700, color:C.navText }}>{avg}</div>
                        <div style={{ fontSize:10, color:C.muted }}>tasks / day</div>
                      </div>
                      <div style={{ background:C.inputBg, borderRadius:12, padding:"12px 14px" }}>
                        <div style={{ fontSize:10, color:C.muted, fontWeight:800, marginBottom:4 }}>COMPLETION RATE</div>
                        <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:700, color:completionRate>=70?C.accent2:completionRate>=40?C.gold:C.coral }}>{completionRate}%</div>
                        <div style={{ fontSize:10, color:C.muted }}>tasks finished</div>
                      </div>
                      {bestDay && (
                        <div style={{ background:`linear-gradient(130deg,${C.coral}18,${C.gold}18)`, border:`1.5px solid ${C.gold}44`, borderRadius:12, padding:"12px 14px", gridColumn:"1/-1" }}>
                          <div style={{ fontSize:10, color:C.muted, fontWeight:800, marginBottom:4 }}>BEST DAY THIS WEEK</div>
                          <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:18, fontWeight:700, color:C.navText }}>
                            {bestDay[0]} <span style={{ color:C.gold }}>({bestDay[1]} tasks) 🏆</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </>
          )}

          {/* ── CHEST ── */}
          {tab==="chest" && (
            <>
              <div style={{ display:"flex", gap:8, marginBottom:14 }}>
                <button style={{ padding:"12px 16px", borderRadius:16, border:"2.5px solid #00897B", background:"#00897B", color:"#fff", fontSize:11, fontWeight:800, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                  <span style={{ fontSize:22 }}>🐚</span>
                  <span style={{ fontSize:10 }}>{chestNames.treasure || "Life"}</span>
                  <span style={{ fontSize:8, color:"rgba(255,255,255,0.7)", letterSpacing:1.5, textTransform:"uppercase" }}>Space</span>
                </button>
                <button disabled style={{ flex:1, padding:"12px 10px", borderRadius:16, border:"1.5px dashed rgba(255,255,255,0.25)", background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.4)", fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", gap:6, cursor:"default" }}>
                  <span>More spaces coming soon 🌊</span>
                </button>
              </div>
              <button onClick={()=>{ setShowSuggestions(!showSuggestions); setOpenCat(null); setSelectedSuggestions([]); }}
                style={{ width:"100%", marginBottom:14, padding:"13px 16px", background:showSuggestions?C.inputBg:`linear-gradient(130deg,#00897B,#26C6DA)`, border:showSuggestions?`1.5px solid ${C.inputBorder}`:"none", borderRadius:16, color:showSuggestions?C.navText:"#fff", fontSize:14, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", gap:9, boxShadow:showSuggestions?"none":"0 3px 12px rgba(0,137,123,0.4)" }}>
                <span style={{ fontSize:20 }}>💡</span>{showSuggestions?"Hide suggestions ↑":"Need inspiration? Get a suggestion →"}
              </button>

              {showSuggestions && (
                <div style={{ marginBottom:16, animation:"slideUp .25s ease" }}>
                  {(SUGGESTIONS[activeChest] || SUGGESTIONS.treasure).map(cat => (
                    <div key={cat.cat} style={{ marginBottom:10 }}>
                      <button onClick={()=>setOpenCat(openCat===cat.cat?null:cat.cat)}
                        style={{ width:"100%", padding:"13px 16px", background:openCat===cat.cat?cat.color:C.card, border:`1.5px solid ${openCat===cat.cat?cat.color:C.cardBorder}`, borderRadius:openCat===cat.cat?"16px 16px 0 0":16, color:openCat===cat.cat?"#fff":C.navText, fontSize:14, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span>{cat.cat}</span><span style={{ fontSize:12, opacity:.75 }}>{openCat===cat.cat?"▲":"▼"}</span>
                      </button>
                      {openCat===cat.cat && (
                        <div style={{ background:"#fff", border:`1.5px solid ${cat.color}44`, borderTop:"none", borderRadius:"0 0 16px 16px", overflow:"hidden" }}>
                          {cat.tasks.map((task,i) => {
                            const sel = selectedSuggestions.includes(task);
                            return (
                              <button key={i} onClick={()=>setSelectedSuggestions(prev => sel ? prev.filter(t=>t!==task) : [...prev, task])}
                                style={{ width:"100%", padding:"12px 16px", background:sel?cat.color+"18":"transparent", border:"none", borderBottom:i<cat.tasks.length-1?`1px solid ${cat.color}18`:"none", color:C.navText, fontSize:13.5, fontWeight:600, textAlign:"left", display:"flex", alignItems:"center", gap:11 }}>
                                <span style={{ color:cat.color, fontSize:18, fontWeight:900, minWidth:20 }}>{sel?"✓":"+"}</span>{task}
                              </button>
                            );
                          })}
                          <div style={{ padding:"10px 12px", borderTop:`1px solid ${cat.color}22`, display:"flex", gap:8 }}>
                            <button onClick={()=>{ if(selectedSuggestions.length>0) addSelectedSuggestions(selectedSuggestions); else addSelectedSuggestions(cat.tasks); }}
                              style={{ flex:1, padding:"10px", background:cat.color, border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800 }}>
                              {selectedSuggestions.length>0 ? `Add ${selectedSuggestions.length} selected →` : "Add all to chest →"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {firstVisit && ideas.length===0 && (
                <div style={{ background:"rgba(255,255,255,0.14)", borderRadius:18, padding:"16px 20px", marginBottom:14, textAlign:"center", border:"1.5px dashed rgba(255,255,255,0.4)", animation:"popIn .4s ease" }}>
                  <div style={{ color:"#fff", fontSize:13.5, fontWeight:700, lineHeight:1.65 }}>
                    Welcome aboard{myName?`, ${myName}`:""}! 🌊 💪<br/>
                    <span style={{ fontWeight:500, opacity:.85 }}>This is your personal hold for every spark and task you find.</span>
                  </div>
                </div>
              )}


              <div style={s.card}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <textarea style={s.textarea} placeholder={CHEST_LABELS[labelIdx]} value={newIdea}
                    onChange={e=>{ setNewIdea(e.target.value); parseNaturalDate(e.target.value); }}
                    onFocus={rotateLabel}
                    onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); addIdea(); } }}
                  />
                </div>
                {parsedDate && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, background:"#E0F7FA", borderRadius:12, padding:"10px 14px", marginBottom:10, animation:"slideUp .2s ease" }}>
                    <span style={{ fontSize:16 }}>📅</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:800, color:"#002D2F" }}>
                        {parsedText && parsedText !== newIdea.trim() && <span style={{ color:"#00897B" }}>"{parsedText}" </span>}
                        due {parsedTime && <span style={{ color:"#FF8A71" }}>{parsedTime} </span>}
                        {new Date(parsedDate+"T12:00:00").toLocaleDateString("en",{weekday:"long",month:"short",day:"numeric"})}
                      </div>
                      <div style={{ fontSize:10, color:"#4a8c8e", marginTop:2 }}>Detected automatically — tap Add to confirm</div>
                    </div>
                    <button onClick={()=>{ setParsedDate(null); setParsedTime(null); setParsedText(""); }}
                      style={{ background:"none", border:"none", color:"#aaa", fontSize:16, cursor:"pointer" }}>✕</button>
                  </div>
                )}
                <button style={s.btn()} onClick={addIdea} disabled={!newIdea.trim()}>
                  "✨ Add to my space"
                </button>
              </div>

              {shuffled && (
                <div style={{ background:`linear-gradient(130deg,${C.coral},#FF5252)`, borderRadius:20, padding:"18px 20px", marginBottom:14, animation:"popIn .3s ease", boxShadow:"0 4px 20px rgba(255,138,113,0.4)" }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:8, fontWeight:800 }}>🎲 Random task</div>
                  <div style={{ color:"#fff", fontSize:16, fontWeight:800, lineHeight:1.5, marginBottom:12 }}>{shuffled.text}</div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ flex:1, padding:"10px", background:"rgba(255,255,255,0.25)", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800 }} onClick={()=>{ toggleDone(shuffled.id); setShuffled(null); }}>✓ Done!</button>
                    <button style={{ flex:1, padding:"10px", background:"rgba(255,255,255,0.15)", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800 }} onClick={doShuffle}>🔀 Another</button>
                    <button style={{ padding:"10px 14px", background:"rgba(255,255,255,0.1)", border:"none", borderRadius:12, color:"#fff", fontSize:13 }} onClick={()=>setShuffled(null)}>✕</button>
                  </div>
                </div>
              )}

              {activeCnt > 1 && !shuffled && (
                <button onClick={doShuffle} style={{ width:"100%", marginBottom:14, padding:"12px", background:"rgba(255,255,255,0.12)", border:"1.5px dashed rgba(255,255,255,0.35)", borderRadius:16, color:"rgba(255,255,255,0.9)", fontSize:13, fontWeight:800 }}>
                  🎲 Pick a random task for me
                </button>
              )}

              {chestIdeas.filter(i=>!i.done).length === 0 && chestIdeas.filter(i=>i.done).length === 0 && (
                <div style={s.empty}>
                  <div style={{ animation:"bob 2s infinite", display:"inline-block", marginBottom:12 }}><Crab size={52} smiling={false}/></div>
                  <br/>Nothing here yet — add your first task above! 🐚
                </div>
              )}

              {/* Today's focus pinned at top */}
              {focusTaskId && chestIdeas.find(i=>i.id===focusTaskId&&!i.done) && (()=>{
                const focused = chestIdeas.find(i=>i.id===focusTaskId&&!i.done);
                return (
                  <div style={{ background:`linear-gradient(130deg,${C.accent2}18,${C.gold}18)`, border:`1.5px solid ${C.accent2}44`, borderRadius:16, padding:"12px 14px", marginBottom:14, animation:"slideUp .25s ease" }}>
                    <div style={{ fontSize:10, color:C.accent2, fontWeight:800, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>🎯 Today's focus</div>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={s.check(false)} onClick={()=>toggleDone(focused.id)}/>
                      <div style={{ flex:1, fontSize:15, fontWeight:700, color:C.navText }}>{focused.text}</div>
                      <button onClick={()=>setFocusTask(null)} style={{ background:"none", border:"none", color:"#aaa", fontSize:14, cursor:"pointer" }}>✕</button>
                    </div>
                  </div>
                );
              })()}

              {chestIdeas.filter(i=>!i.done).map((idea) => (
                <div key={idea.id} className="idea-row" style={{ ...s.item(false), opacity: focusTaskId && focusTaskId !== idea.id ? 0.85 : 1 }} onClick={()=>openMenu===idea.id&&setOpenMenu(null)}>
                  <div style={s.check(false)} onClick={e=>{e.stopPropagation();toggleDone(idea.id);}}/>
                  <div style={{ flex:1 }} onClick={()=>{ if(editingIdea!==idea.id){ setEditingIdea(idea.id); setEditingText(idea.text); setEditingDueInEdit(idea.due_date||""); setEditingTimeInEdit(idea.due_time||""); } }}>
                    {editingIdea===idea.id ? (
                      <div onClick={e=>e.stopPropagation()}>
                        <input style={{ ...s.input, flex:"none", width:"100%", marginBottom:8 }} value={editingText}
                          onChange={e=>setEditingText(e.target.value)}
                          onKeyDown={e=>{ if(e.key==="Escape") setEditingIdea(null); }}
                          autoFocus/>
                        <div style={{ display:"flex", gap:6, marginBottom:8 }}>
                          <input type="date" style={{ flex:1, background:C.inputBg, border:`1.5px solid ${C.inputBorder}`, borderRadius:10, padding:"8px 10px", color:C.navText, fontSize:12, outline:"none" }}
                            value={editingDueInEdit} onChange={e=>setEditingDueInEdit(e.target.value)}/>
                          <input type="time" style={{ flex:1, background:C.inputBg, border:`1.5px solid ${C.inputBorder}`, borderRadius:10, padding:"8px 10px", color:C.navText, fontSize:12, outline:"none" }}
                            value={editingTimeInEdit} onChange={e=>setEditingTimeInEdit(e.target.value)}/>
                        </div>
                        <div style={{ display:"flex", gap:6 }}>
                          <button style={{ ...s.smBtn(C.accent2), flex:1, padding:"8px", textAlign:"center" }} onClick={()=>saveEditIdea(idea.id)}>Save</button>
                          <button style={{ ...s.smBtn(C.muted), padding:"8px 12px" }} onClick={()=>setEditingIdea(null)}>Cancel</button>
                          {editingDueInEdit && <button style={{ ...s.smBtn(C.danger), padding:"8px 12px" }} onClick={()=>{ setEditingDueInEdit(""); setEditingTimeInEdit(""); }}>Clear date</button>}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={s.iText(false)}>{idea.text}</div>
                        <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap", alignItems:"center" }}>
                          {idea.from_friend && <span style={{ fontSize:10, color:C.accent2, fontWeight:700 }}>from {idea.from_friend}</span>}
                          {idea.due_date && !isNaN(new Date(idea.due_date)) && <span style={{ fontSize:10, color: new Date(idea.due_date+"T23:59:59") < new Date() ? C.danger : C.muted, fontWeight:700 }}>📅 {idea.due_time ? `${idea.due_time} · ` : ""}{new Date(idea.due_date+"T12:00:00").toLocaleDateString("en",{weekday:"short",month:"short",day:"numeric"})}</span>}
                          {idea.repeatable && <span style={{ fontSize:10, color:C.gold, fontWeight:700 }}>repeating</span>}
                          {idea.id===focusTaskId && <span style={{ fontSize:10, color:C.accent2, fontWeight:700 }}>🎯 focus</span>}
                        </div>
                      </>
                    )}
                    <div style={s.meta}>{idea.ts}</div>
                  </div>
                  <div style={{ position:"relative", flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>setOpenMenu(openMenu===idea.id?null:idea.id)}
                      style={{ background:"none", border:"none", color:"#aaa", fontSize:18, cursor:"pointer", padding:"0 4px", lineHeight:1 }}>···</button>
                    {openMenu===idea.id && (
                      <div style={{ position:"absolute", right:0, bottom:"100%", background:"#fff", border:`1px solid ${C.cardBorder}`, borderRadius:12, boxShadow:"0 4px 20px rgba(0,0,0,0.12)", zIndex:100, minWidth:180, overflow:"hidden", marginBottom:4 }}>
                        <button onClick={()=>{ setEditingIdea(idea.id); setEditingText(idea.text); setEditingDueInEdit(idea.due_date||""); setEditingTimeInEdit(idea.due_time||""); setOpenMenu(null); }}
                          style={{ width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"left", fontSize:13, fontWeight:700, color:C.navText, cursor:"pointer", borderBottom:`1px solid ${C.cardBorder}` }}>
                          Edit task
                        </button>
                        <button onClick={()=>setFocusTask(idea.id)}
                          style={{ width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"left", fontSize:13, fontWeight:700, color:focusTaskId===idea.id?C.accent2:C.navText, cursor:"pointer", borderBottom:`1px solid ${C.cardBorder}` }}>
                          {focusTaskId===idea.id ? "Remove focus" : "Set as today's focus"}
                        </button>
                        <button onClick={()=>{ toggleRepeatable(idea.id); setOpenMenu(null); }}
                          style={{ width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"left", fontSize:13, fontWeight:700, color:idea.repeatable?C.gold:C.navText, cursor:"pointer", borderBottom:`1px solid ${C.cardBorder}` }}>
                          {idea.repeatable ? "Turn off repeat" : "Set to repeat"}
                        </button>
                        <button onClick={()=>{ deleteIdea(idea.id); setOpenMenu(null); }}
                          style={{ width:"100%", padding:"11px 16px", background:"none", border:"none", textAlign:"left", fontSize:13, fontWeight:700, color:C.danger, cursor:"pointer" }}>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {doneCnt > 0 && (
                <>
                  <button onClick={()=>setShowDone(v=>!v)} style={{ width:"100%", marginBottom:0, padding:"11px 16px", background:"rgba(255,255,255,0.1)", border:"1.5px solid rgba(255,255,255,0.2)", borderRadius:showDone?"14px 14px 0 0":14, color:"rgba(255,255,255,0.85)", fontSize:13, fontWeight:800, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span>✅ {doneCnt} completed</span>
                    <span style={{ fontSize:11, opacity:.7 }}>{showDone?"▲ hide":"▼ show"}</span>
                  </button>
                  {showDone && (
                    <div style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.15)", borderTop:"none", borderRadius:"0 0 14px 14px", overflow:"hidden", animation:"slideUp .2s ease" }}>
                      {chestIdeas.filter(i=>i.done).map((idea,idx,arr) => (
                        <div key={idea.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:idx<arr.length-1?"1px solid rgba(255,255,255,0.1)":"none" }}>
                          <div style={{ ...s.check(true), flexShrink:0, width:22, height:22, fontSize:11 }} onClick={()=>toggleDone(idea.id)}>✓</div>
                          <div style={{ flex:1, fontSize:13, color:"rgba(255,255,255,0.55)", textDecoration:"line-through", lineHeight:1.4 }}>{idea.text}</div>
                          <button style={{ ...s.del, color:"rgba(255,255,255,0.35)", fontSize:14 }} onClick={()=>deleteIdea(idea.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── CREW ── */}
          {tab==="bounty" && (
            <>
              <div style={{ ...s.advCard, textAlign:"center", padding:"22px 20px 18px" }}>
                <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"center", gap:10, marginBottom:12 }}>
                  <div style={{ animation:"bob 2.4s infinite ease-in-out" }}><Mermaid size={52}/></div>
                  <div style={{ animation:"bob 2s infinite ease-in-out", animationDelay:".3s" }}><Crab size={58} smiling={justSmiled}/></div>
                  <div style={{ animation:"bob 2.6s infinite ease-in-out", animationDelay:".6s" }}><Octopus size={52}/></div>
                  <div style={{ animation:"bob 1.9s infinite ease-in-out", animationDelay:".15s" }}><Dolphin size={52}/></div>
                </div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:700, fontSize:19, color:C.navText, marginBottom:6 }}>Your friends 🌊</div>
                <div style={{ fontSize:13, color:C.muted, lineHeight:1.7 }}>Send tasks, encouragement, and support to the people you trust. 🌿</div>
              </div>

              {/* Friend requests */}
              {friendRequests.length>0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10, fontWeight:800 }}>👋 Friend requests</div>
                  {friendRequests.map(req => (
                    <div key={req.code} className="idea-row" style={{ ...s.item(false), background:"#FFF8E1", borderColor:"#FFD54F" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:800, color:C.navText }}>{req.label}</div>
                        <div style={s.meta}>wants to connect with you</div>
                      </div>
                      <div style={{ display:"flex", gap:7, flexShrink:0 }}>
                        <button style={s.smBtn(C.accent2)} onClick={()=>acceptFriendRequest(req)}>Accept</button>
                        <button style={s.smBtn(C.danger)} onClick={()=>declineFriendRequest(req)}>Decline</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Sent requests pending */}
              {sentRequests.length>0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10, fontWeight:800 }}>⏳ Pending requests</div>
                  {sentRequests.map(req => (
                    <div key={req.code} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"rgba(255,255,255,0.08)", borderRadius:12, marginBottom:8 }}>
                      <div style={{ fontSize:13, color:"rgba(255,255,255,0.8)", fontWeight:700 }}>{req.label}</div>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>Waiting...</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Incoming tasks/messages */}
              {incoming.length>0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.7)", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10, fontWeight:800 }}>💌 From your friends</div>
                  {incoming.map(item => (
                    <div key={item.id} className="idea-row" style={{ ...s.item(false), background:"#E8F5FD", borderColor:"#B3E5FC" }}>
                      <div style={{ flex:1 }}>
                        <div style={s.iText(false)}>{item.text}</div>
                        <div style={s.meta}>from {item.from_name||item.from_code} · {item.ts}</div>
                      </div>
                      <div style={{ display:"flex", gap:7, flexShrink:0 }}>
                        <button style={s.smBtn(C.accent2)} onClick={()=>acceptIncoming(item)}>Add</button>
                        <button style={s.smBtn(C.danger)} onClick={()=>dismissIncoming(item)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={s.card}>
                <span style={s.label}>Your friend code</span>
                <div style={{ marginBottom:16 }}>
                  {editingMyName ? (
                    <div style={s.row}>
                      <input style={s.input} value={tempName} onChange={e=>setTempName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&tempName.trim()&&saveUsername(tempName)} placeholder="Your name" autoFocus/>
                      <button style={{ ...s.btn(C.accent2), width:"auto", marginTop:0, padding:"10px 14px" }} onClick={()=>tempName.trim()&&saveUsername(tempName)}>Save</button>
                      <button style={{ ...s.btn(C.inputBg,C.muted), width:"auto", marginTop:0, padding:"10px 14px", boxShadow:"none" }} onClick={()=>setEditingMyName(false)}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ fontSize:15, fontWeight:800, color:C.navText }}>{myName?`👤 ${myName}`:<span style={{ color:C.muted, fontWeight:600 }}>No name set yet</span>}</div>
                      <button style={s.smBtn(C.muted)} onClick={()=>{ setTempName(myName); setEditingMyName(true); }}>✏️ {myName?"Edit":"Add name"}</button>
                    </div>
                  )}
                </div>
                <p style={{ fontSize:13, color:C.muted, marginBottom:16, lineHeight:1.7 }}>Share this code with friends so they can connect with you. You can send each other tasks and encouragement. 🦀</p>
                <div style={{ background:C.inputBg, border:`2px dashed ${C.coral}`, borderRadius:14, padding:"16px 18px", marginBottom:14, textAlign:"center" }}>
                  <div style={{ fontSize:10, color:C.muted, letterSpacing:3, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>Your code</div>
                  <div style={s.codeVal}>{myCode}</div>
                </div>
                <button style={{ ...s.outline, width:"100%", fontSize:13, color:C.coral, borderColor:C.coral+"66", fontWeight:800 }} onClick={()=>{ navigator.clipboard?.writeText(myCode); showToast("Code copied! 📋"); }}>📋 Copy code</button>
              </div>

              <div style={s.divider}>— find a friend —</div>

              <div style={s.card}>
                <span style={s.label}>Search by name or code</span>
                <div style={s.row}>
                  <input style={s.input} placeholder="Username or 4-letter code..."
                    value={searchQuery||joinCode}
                    onChange={e=>{
                      const val = e.target.value;
                      if(/^[A-Z0-9]{1,4}$/.test(val.toUpperCase()) && val.length<=4 && !/[a-z_]/.test(val)) {
                        setJoinCode(val.toUpperCase()); setSearchQuery("");
                      } else {
                        setSearchQuery(val); setJoinCode(""); setSearchResults([]);
                      }
                    }}
                    onKeyDown={e=>{ if(e.key==="Enter"){ if(joinCode.length===4) joinFriend(); else doSearchFriend(); } }}
                  />
                  <button style={{ ...s.btn(C.coral), width:"auto", marginTop:0, padding:"12px 20px" }}
                    onClick={()=>{ if(joinCode.length===4) joinFriend(); else doSearchFriend(); }}>
                    {searching?"...":"Find"}
                  </button>
                </div>
                <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>Enter a username to search, or paste a 4-letter code to connect directly</div>
                {searchResults.length>0 && (
                  <div style={{ marginTop:12 }}>
                    {searchResults.map(r => (
                      <div key={r.code} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${C.cardBorder}` }}>
                        <div>
                          <div style={{ fontWeight:800, fontSize:14, color:C.navText }}>👤 {r.username}</div>
                          <div style={{ fontSize:11, color:C.muted, fontFamily:"monospace", letterSpacing:2 }}>{r.code}</div>
                        </div>
                        <button style={s.smBtn(C.coral)} onClick={()=>joinFriend(r.code, r.username)}>+ Add</button>
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.length===0 && searchQuery && !searching && (
                  <div style={{ fontSize:13, color:C.muted, marginTop:10, textAlign:"center" }}>No results — try their exact username</div>
                )}
              </div>

              {friendBoxes.length===0 ? (
                <div style={s.empty}>
                  <div style={{ display:"flex", justifyContent:"center", gap:12, marginBottom:16 }}>
                    <div style={{ animation:"bob 2s infinite" }}><Dolphin size={44}/></div>
                    <div style={{ animation:"bob 2.4s infinite", animationDelay:".3s" }}><Mermaid size={44}/></div>
                  </div>
                  No friends yet.<br/><span style={{ opacity:.75 }}>Enter their code or search by username above!</span>
                </div>
              ) : friendBoxes.map(box => (
                <div key={box.code} style={s.card}>
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ fontWeight:800, fontSize:15, color:C.navText }}>⚓ {box.label}</div>
                      <button style={{ ...s.smBtn(C.danger), fontSize:11 }} onClick={()=>removeFriend(box.code)}>Remove</button>
                    </div>
                  </div>
                  {friendTarget===box.code ? (
                    <>
                      <textarea style={{ ...s.textarea, minHeight:76 }} placeholder="Send them a task, a kind word, or a gentle nudge..." value={friendIdea} onChange={e=>setFriendIdea(e.target.value)}/>
                      <div style={s.row}>
                        <button style={{ ...s.btn(C.coral), flex:1, marginTop:10 }} onClick={()=>sendToFriend(box.code)} disabled={!friendIdea.trim()}>💌 Send their way</button>
                        <button style={{ ...s.btn(C.inputBg,C.muted), width:"auto", marginTop:10, padding:"14px 16px", boxShadow:"none", border:`1.5px solid ${C.inputBorder}` }} onClick={()=>{ setFriendTarget(null); setFriendIdea(""); }}>✕</button>
                      </div>
                    </>
                  ) : (
                    <button style={{ ...s.outline, width:"100%", color:C.coral, borderColor:C.coral+"55", fontWeight:800 }} onClick={()=>setFriendTarget(box.code)}>💌 Send them a task or encouragement</button>
                  )}
                </div>
              ))}
            </>
          )}

        </div>
      </div>

      {/* ── BOTTOM TAB BAR ── */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"#fff", borderTop:"1px solid #E0F7FA", zIndex:200 }}><div style={{ display:"flex", maxWidth:480, margin:"0 auto", paddingBottom:"env(safe-area-inset-bottom)" }}>
        {[
          { key:"chest",  label:"Tasks",    icon:"🐚" },
          { key:"map",    label:"Progress", icon:"🗺️" },
          { key:"bounty", label:"Friends",  icon:"🌊", badge: incoming.length + friendRequests.length },
        ].map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ flex:1, padding:"10px 4px 8px", background:"transparent", border:"none", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:3, position:"relative" }}>
            <span style={{ fontSize:20, opacity:tab===t.key?1:0.45, transition:"all .2s" }}>{t.icon}</span>
            <span style={{ fontSize:10, fontWeight:tab===t.key?800:600, color:tab===t.key?C.accent2:C.muted, transition:"all .2s" }}>{t.label}</span>
            {t.badge>0 && <div style={{ position:"absolute", top:6, right:"calc(50% - 14px)", background:C.coral, borderRadius:50, width:16, height:16, fontSize:9, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:900 }}>{t.badge}</div>}
            {tab===t.key && <div style={{ position:"absolute", top:0, left:"20%", right:"20%", height:2, background:C.accent2, borderRadius:50 }}/>}
          </button>
        ))}
      </div></div>

      <div style={s.toast(toast.visible)}>{toast.msg}</div>

      {/* ── SHARE CARD MODAL ── */}
      {showShareCard && <ShareCardModal
        show={showShareCard}
        onClose={()=>setShowShareCard(false)}
        myName={myName}
        totalDone={totalDone}
        streak={streak}
        stage={stage}
        myCreature={myCreature}
        C={C}
        showToast={showToast}
      />}


      {/* ── SETTINGS ── */}
      {showSettings && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={()=>setShowSettings(false)}>
          <div style={{ background:"linear-gradient(170deg,#E0F7FA,#fff)", borderRadius:"24px 24px 0 0", padding:"0 20px 40px", maxWidth:480, width:"100%", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 -8px 40px rgba(0,0,0,0.2)", animation:"slideUp .3s ease" }} onClick={e=>e.stopPropagation()}>

            <div style={{ display:"flex", justifyContent:"center", padding:"14px 0 6px" }}>
              <div style={{ width:40, height:4, borderRadius:50, background:"#C8F0F5" }}/>
            </div>

            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div style={{ fontFamily:"'Fredoka',sans-serif", fontSize:22, fontWeight:700, color:"#002D2F" }}>
                <CreatureWithAccessory id={myCreature} size={36} hatId={myHat}/> Settings
              </div>
              <button onClick={()=>setShowSettings(false)} style={{ background:"none", border:"none", fontSize:20, color:"#aaa", cursor:"pointer" }}>✕</button>
            </div>

            {/* Account */}
            <div style={{ background:"rgba(0,45,47,0.04)", borderRadius:14, padding:"12px 16px", marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:8, fontWeight:800 }}>Account</div>
              <div style={{ fontSize:13, color:"#002D2F", fontWeight:700, marginBottom:6 }}>📧 {user.email}</div>
              <div style={{ fontSize:12, color:"#4a8c8e", marginBottom:10 }}>
                Your code: <span style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:700, letterSpacing:3, color:"#FF8A71" }}>{myCode}</span>
              </div>
              {showRelinkCode ? (
                <div style={{ marginBottom:10 }}>
                  <input
                    placeholder="Enter old code (e.g. A3X9)"
                    value={relinkCode}
                    onChange={e=>{ setRelinkCode(e.target.value.toUpperCase()); setRelinkError(""); }}
                    maxLength={4}
                    style={{ width:"100%", background:"#E0F7FA", border:`1.5px solid ${relinkError?"#E53935":"#80DEEA"}`, borderRadius:10, padding:"10px 14px", color:"#002D2F", fontSize:16, outline:"none", marginBottom:6, fontFamily:"'Fredoka',sans-serif", letterSpacing:6, textAlign:"center", fontWeight:700 }}
                  />
                  {relinkError && <div style={{ color:"#E53935", fontSize:11, fontWeight:700, marginBottom:6 }}>{relinkError}</div>}
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={handleRelink} disabled={relinkLoading}
                      style={{ flex:1, padding:"9px", background:"#00897B", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                      {relinkLoading ? "Linking..." : "✓ Link it"}
                    </button>
                    <button onClick={()=>{ setShowRelinkCode(false); setRelinkCode(""); setRelinkError(""); }}
                      style={{ padding:"9px 14px", background:"transparent", border:"1.5px solid #ccc", borderRadius:10, color:"#aaa", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={()=>setShowRelinkCode(true)}
                  style={{ width:"100%", padding:"9px", background:"transparent", border:"1.5px solid #80DEEA", borderRadius:10, color:"#4a8c8e", fontSize:12, fontWeight:800, cursor:"pointer", marginBottom:8 }}>
                  🔗 Link an old code to recover data
                </button>
              )}
              <button onClick={handleLogout} style={{ width:"100%", padding:"11px", background:"transparent", border:"1.5px solid #E53935", borderRadius:12, color:"#E53935", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                🚪 Log out
              </button>
              {confirmDelete ? (
                <div style={{ marginTop:10 }}>
                  <div style={{ fontSize:12, color:"#E53935", fontWeight:700, marginBottom:8, lineHeight:1.5 }}>
                    This permanently deletes your account and all your tasks. This can't be undone.
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={handleDeleteAccount} disabled={deleting}
                      style={{ flex:1, padding:"10px", background:"#E53935", border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer" }}>
                      {deleting ? "Deleting..." : "Yes, delete everything"}
                    </button>
                    <button onClick={()=>setConfirmDelete(false)} disabled={deleting}
                      style={{ padding:"10px 14px", background:"transparent", border:"1.5px solid #ccc", borderRadius:10, color:"#aaa", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={()=>setConfirmDelete(true)}
                  style={{ width:"100%", padding:"9px", marginTop:8, background:"transparent", border:"none", color:"#aaa", fontSize:12, fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>
                  Delete my account
                </button>
              )}
            </div>

            {/* Creature picker */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Your crew member</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:20 }}>
              {CREATURES.map(c => {
                const unlocked = totalDone >= c.unlockReq;
                const active = myCreature===c.id;
                return (
                  <button key={c.id} onClick={()=>{ if(unlocked) pickCreature(c.id); else showToast(`🔒 Reach ${c.unlockDesc} to unlock!`); }}
                    style={{ background:active?"#FF8A7122":"rgba(0,45,47,0.04)", border:`2px solid ${active?"#FF8A71":unlocked?"#B2DFDB":"#eee"}`, borderRadius:14, padding:"10px 6px", display:"flex", flexDirection:"column", alignItems:"center", gap:5, cursor:unlocked?"pointer":"default", opacity:unlocked?1:.45, transition:"all .2s" }}>
                    <CreatureIcon id={c.id} size={36} smiling={active}/>
                    <div style={{ fontSize:10, fontWeight:800, color:active?"#FF8A71":"#002D2F" }}>{c.name}</div>
                    {!unlocked && <div style={{ fontSize:9, color:"#4a8c8e", fontWeight:700 }}>🔒 {c.unlockDesc}</div>}
                  </button>
                );
              })}
            </div>

            {/* Hat picker */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Legendary hats 🎩</div>
            {totalDone < STAGES[7].req ? (
              <div style={{ background:"rgba(0,45,47,0.06)", borderRadius:14, padding:"14px 16px", marginBottom:20, textAlign:"center" }}>
                <div style={{ fontSize:24, marginBottom:6 }}>🔒</div>
                <div style={{ fontSize:12, color:"#4a8c8e", lineHeight:1.6 }}>Complete all 8 islands to unlock!<br/><span style={{ fontWeight:800, color:"#FF8A71" }}>{STAGES[7].req - totalDone} tasks to go</span></div>
              </div>
            ) : (
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
                {HATS.map(hat => {
                  const active = myHat===hat.id;
                  return (
                    <button key={hat.id} onClick={async()=>{ setMyHat(hat.id); await saveProfile(user.id, { hat: hat.id }); showToast(hat.id==="none"?"Hat removed":`${hat.emoji} ${hat.label} equipped!`); }}
                      style={{ padding:"10px 14px", borderRadius:14, border:`2px solid ${active?"#FF8A71":"#B2DFDB"}`, background:active?"#FF8A7118":"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:5, transition:"all .2s", minWidth:70 }}>
                      {hat.id==="none" ? <span style={{ fontSize:22, opacity:.5 }}>∅</span> : <CreatureWithAccessory id={myCreature} size={44} hatId={hat.id}/>}
                      <span style={{ fontSize:10, fontWeight:800, color:active?"#FF8A71":"#002D2F", whiteSpace:"nowrap" }}>{hat.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Rename spaces */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Rename chests</div>
            <div style={{ background:"rgba(0,45,47,0.04)", borderRadius:14, padding:"12px 14px", marginBottom:20 }}>
              {CHESTS.map(ch => {
                const unlocked = chestUnlocked(ch);
                return (
                  <div key={ch.id} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    {editingChestName===ch.id ? (
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", gap:6, marginBottom:8 }}>
                          <input autoFocus style={{ flex:1, background:"#E0F7FA", border:"1.5px solid #80DEEA", borderRadius:10, padding:"8px 12px", color:"#002D2F", fontSize:13, outline:"none" }}
                            value={tempChestName} onChange={e=>setTempChestName(e.target.value)}
                            onKeyDown={e=>{ if(e.key==="Enter") saveChestName(ch.id, tempChestName, tempChestEmoji); if(e.key==="Escape") setEditingChestName(null); }}/>
                          <button style={{ padding:"6px 12px", background:"#00897B", border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800 }} onClick={()=>saveChestName(ch.id, tempChestName, tempChestEmoji)}>Save</button>
                        </div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:4 }}>
                          {['🐚','💼','✨','🎯','🌊','💡','🔥','⚡','🎨','🌸','🍀','⭐','🎵','🏆','🌺','💎','🦋','🌙','☀️','🐠'].map(em=>(
                            <button key={em} onClick={()=>setTempChestEmoji(em)}
                              style={{ fontSize:20, background:tempChestEmoji===em?"#E0F7FA":"transparent", border:`1.5px solid ${tempChestEmoji===em?"#80DEEA":"transparent"}`, borderRadius:8, padding:"2px 4px", cursor:"pointer" }}>
                              {em}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span style={{ fontWeight:800, fontSize:14, color:unlocked?"#002D2F":"#aaa" }}>{chestEmojis[ch.id]||ch.emoji} {chestNames[ch.id]}{!unlocked&&" 🔒"}</span>
                        {unlocked && <button style={{ padding:"5px 10px", background:"#E0F7FA", border:"1.5px solid #80DEEA55", borderRadius:8, color:"#4a8c8e", fontSize:11, fontWeight:800 }} onClick={()=>{ setEditingChestName(ch.id); setTempChestName(chestNames[ch.id]); setTempChestEmoji(chestEmojis[ch.id]||ch.emoji); }}>✏️</button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Notifications */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Notifications</div>
            <div style={{ background:"rgba(0,45,47,0.04)", borderRadius:14, padding:"4px 14px", marginBottom:20 }}>
              {[
                { key:"dailyNudge", label:"Daily check-in nudge", emoji:"🌅" },
                { key:"streakReminder", label:"Streak reminder at 8pm", emoji:"🔥" },
                { key:"friendTask", label:"Friend sends me a task", emoji:"💌" },
                { key:"dueDateReminder", label:"Due date reminders at 9am", emoji:"📅" },
                { key:"weeklySummary", label:"Weekly summary (Sunday)", emoji:"📊" },
              ].map(item => (
                <div key={item.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderBottom:"1px solid #E0F7FA" }}>
                  <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                    <span style={{ fontSize:18 }}>{item.emoji}</span>
                    <span style={{ fontWeight:800, fontSize:13, color:"#002D2F" }}>{item.label}</span>
                  </div>
                  <button onClick={async()=>{
                    if (!notifPrefs[item.key]) {
                      if ("Notification" in window && Notification.permission !== "granted") {
                        await Notification.requestPermission();
                      }
                      if (window.OneSignal) {
                        window.OneSignalDeferred = window.OneSignalDeferred || [];
                        window.OneSignalDeferred.push(async (OneSignal) => {
                          await OneSignal.Notifications.requestPermission();
                        });
                      }
                    }
                    saveNotifPrefs({ ...notifPrefs, [item.key]: !notifPrefs[item.key] });
                    showToast(notifPrefs[item.key] ? "Off" : "On ✅");
                  }}
                    style={{ width:46, height:26, borderRadius:50, border:"none", background:notifPrefs[item.key]?"#00897B":"#ccc", position:"relative", cursor:"pointer", transition:"background .2s", flexShrink:0 }}>
                    <div style={{ position:"absolute", top:3, left:notifPrefs[item.key]?22:3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left .2s", boxShadow:"0 1px 4px rgba(0,0,0,0.2)" }}/>
                  </button>
                </div>
              ))}
            </div>

            {/* Coming soon */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Coming soon 🌊</div>
            <div style={{ background:"rgba(0,45,47,0.04)", borderRadius:14, padding:"4px 14px", marginBottom:20, opacity:0.6 }}>
              {[
                { emoji:"⌚", label:"Apple Watch app" },
                { emoji:"🎨", label:"Custom themes" },
                { emoji:"📱", label:"Chrome extension" },
                { emoji:"🤖", label:"AI suggestions" },
                { emoji:"🌊", label:"More spaces (Work, Creative)" },
                { emoji:"📊", label:"Weekly digest" },
              ].map(item => (
                <div key={item.label} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 0", borderBottom:"1px solid rgba(0,45,47,0.06)" }}>
                  <span style={{ fontSize:18 }}>{item.emoji}</span>
                  <span style={{ fontWeight:700, fontSize:13, color:"#4a8c8e" }}>{item.label}</span>
                  <span style={{ marginLeft:"auto", fontSize:9, fontWeight:800, color:"#4a8c8e", background:"rgba(0,45,47,0.08)", padding:"3px 8px", borderRadius:50, letterSpacing:1, textTransform:"uppercase" }}>Soon</span>
                </div>
              ))}
            </div>

            {/* Support */}
            <div style={{ fontWeight:800, fontSize:11, color:"#4a8c8e", letterSpacing:2.5, textTransform:"uppercase", marginBottom:10 }}>Support</div>
            <div style={{ background:"rgba(0,45,47,0.04)", borderRadius:14, padding:"12px 14px", marginBottom:20 }}>
              {feedbackSent ? (
                <div style={{ textAlign:"center", padding:"12px 8px", marginBottom:10 }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>🦀</div>
                  <div style={{ fontSize:13, fontWeight:800, color:"#00897B" }}>Thanks — we got it!</div>
                  <button onClick={()=>setFeedbackSent(false)} style={{ marginTop:8, background:"none", border:"none", color:"#4a8c8e", fontSize:12, fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>Send another</button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize:12, color:"#4a8c8e", fontWeight:700, marginBottom:8 }}>🐛 Found a bug or have an idea? Tell us:</div>
                  <textarea
                    value={feedbackText}
                    onChange={e=>setFeedbackText(e.target.value)}
                    placeholder="Type your message..."
                    rows={3}
                    style={{ width:"100%", background:"#fff", border:"1.5px solid #B2DFDB", borderRadius:10, padding:"10px 12px", color:"#002D2F", fontSize:13, outline:"none", resize:"vertical", fontFamily:"'Nunito',sans-serif", marginBottom:8 }}
                  />
                  <button onClick={submitFeedback} disabled={sendingFeedback || !feedbackText.trim()}
                    style={{ width:"100%", padding:"12px", background:"#00897B", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:800, cursor:"pointer", marginBottom:10, opacity:(sendingFeedback||!feedbackText.trim())?0.5:1 }}>
                    {sendingFeedback ? "Sending..." : "Send feedback"}
                  </button>
                </>
              )}
              <div style={{ display:"flex", gap:16, justifyContent:"center" }}>
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ fontSize:12, fontWeight:700, color:"#4a8c8e", textDecoration:"none" }}>Privacy Policy</a>
                <span style={{ color:"#ccc" }}>·</span>
                <a href={TERMS_URL} target="_blank" rel="noreferrer" style={{ fontSize:12, fontWeight:700, color:"#4a8c8e", textDecoration:"none" }}>Terms</a>
              </div>
            </div>

            <div style={{ textAlign:"center", color:"rgba(0,45,47,0.35)", fontSize:11, fontWeight:700, marginTop:8 }}>Kani Do {APP_VERSION} · Made with 🦀</div>
          </div>
        </div>
      )}
    </>
  );
}
